import React, { useState, useEffect, useRef, memo, useCallback } from 'react';
import { PortfolioItem, PortfolioSummary, BISTPrice, CurrencyPrice, GoldPrice, CryptoPrice, SellTransaction } from '../services/types';
import { useAuth } from '../contexts/AuthContext';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import ExcelUpload from './ExcelUpload';
import AssetDetailModal from './AssetDetailModal';
import SellTransactionExcelUpload from './SellTransactionExcelUpload';
import BankDeposit from './BankDeposit';
import { fmtTR } from '../utils/format';

// Re-render'dan tamamen bağımsız USD kuru input bileşeni
const UsdRateInput = memo(({ initialValue, onRateChange, onRefresh }: {
  initialValue: string;
  onRateChange: (value: string) => void;
  onRefresh: () => string;
}) => {
  const [localValue, setLocalValue] = useState(initialValue);
  
  useEffect(() => {
    setLocalValue(initialValue);
  }, [initialValue]);

  return (
    <div className="usd-input-container">
      <input
        type="text"
        inputMode="decimal"
        placeholder="Dolar Kuru (₺)"
        value={localValue}
        onChange={(e) => {
          setLocalValue(e.target.value);
          onRateChange(e.target.value);
        }}
      />
      <button
        type="button"
        onClick={() => {
          const rate = onRefresh();
          setLocalValue(rate);
          onRateChange(rate);
        }}
        className="usd-update-button"
        title="Güncel kur ile güncelle"
      >
        🔄
      </button>
    </div>
  );
});

interface PortfolioProps {
  stocks: BISTPrice[];
  currencies: CurrencyPrice[];
  golds: GoldPrice[];
  cryptos: CryptoPrice[];
}

const Portfolio: React.FC<PortfolioProps> = ({ stocks, currencies, golds, cryptos }) => {
  const { user } = useAuth();
  const [portfolio, setPortfolio] = useState<PortfolioItem[]>([]);

  // Banka Mevduatı kayıtlarını migrate eden fonksiyon (her zaman çalışır)
  function migrateDeposits(items: PortfolioItem[]): PortfolioItem[] {
    return items.map(item => {
      if (item.type === 'deposit') {
        // Yanlış format: quantity = tutar, buyPrice = 1, currentPrice = 1
        if (item.quantity > 1 && (item.buyPrice === 1 || item.currentPrice === 1)) {
          return {
            ...item,
            buyPrice: item.quantity,
            currentPrice: item.quantity,
            quantity: 1,
            interestRate: (item as any).interestRate ?? 0,
            maturityDay: (item as any).maturityDay ?? 0,
            withholdingTax: (item as any).withholdingTax ?? 0,
          };
        }
      }
      return item;
    });
  }

  // Portföy ilk yüklenirken ve her güncellemede migrate et
  useEffect(() => {
    if (!user) return;
    const key = `portfolio_${user.email}`;
    const stored = localStorage.getItem(key);
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed)) {
          const migrated = migrateDeposits(parsed);
          setPortfolio(migrated);
          localStorage.setItem(key, JSON.stringify(migrated));
        } else {
          setPortfolio([]);
        }
      } catch {
        setPortfolio([]);
      }
    } else {
      setPortfolio([]);
    }
  }, [user]);

  // Portföy her güncellendiğinde migrate et ve localStorage'a yaz
  React.useEffect(() => {
    if (!user) return;
    const key = `portfolio_${user.email}`;
    setPortfolio(prev => {
      const migrated = migrateDeposits(prev);
      localStorage.setItem(key, JSON.stringify(migrated));
      return migrated;
    });
  }, [portfolio.length, user]);
  const [sellTransactions, setSellTransactions] = useState<SellTransaction[]>([]);
  const [portfolioLoading, setPortfolioLoading] = useState(true);
  
  // Form verilerini localStorage'dan yükle
  const getStoredNewItem = () => {
    const stored = localStorage.getItem('portfolio_newItem');
    if (stored) {
      try {
        return JSON.parse(stored);
      } catch {
        return {
          type: 'stock' as 'stock' | 'currency' | 'gold' | 'crypto',
          symbol: '',
          quantity: '',
          buyPrice: '',
          buyDate: new Date().toISOString().split('T')[0],
          usdRate: '',
        };
      }
    }
    return {
      type: 'stock' as 'stock' | 'currency' | 'gold' | 'crypto',
      symbol: '',
      quantity: '',
      buyPrice: '',
      buyDate: new Date().toISOString().split('T')[0],
      usdRate: '',
    };
  };

  const getStoredSellItem = () => {
    const stored = localStorage.getItem('portfolio_sellItem');
    if (stored) {
      try {
        return JSON.parse(stored);
      } catch {
        return {
          symbol: '',
          quantity: '',
          sellPrice: '',
          sellDate: new Date().toISOString().split('T')[0],
          usdRate: '',
          buyPrice: 0,
          portfolioItemId: '', // Seçilen spesifik portföy item'ı
        };
      }
    }
    return {
      symbol: '',
      quantity: '',
      sellPrice: '',
      sellDate: new Date().toISOString().split('T')[0],
      usdRate: '',
      buyPrice: 0,
      portfolioItemId: '', // Seçilen spesifik portföy item'ı
    };
  };

  const [newItem, setNewItem] = useState(getStoredNewItem());
  const [sellItem, setSellItem] = useState(getStoredSellItem());
  
  // USD kuru için tamamen ayrı state (diğer state güncellemelerinden bağımsız)
  const [addUsdRate, setAddUsdRate] = useState('');
  const [sellUsdRate, setSellUsdRate] = useState('');
  
  // Form visibility'ı localStorage'dan yükle
  const [showAddForm, setShowAddForm] = useState(() => {
    const stored = localStorage.getItem('portfolio_showAddForm');
    return stored ? JSON.parse(stored) : false;
  });
  
  const [showSellForm, setShowSellForm] = useState(() => {
    const stored = localStorage.getItem('portfolio_showSellForm');
    return stored ? JSON.parse(stored) : false;
  });
  
  // Excel upload modal visibility
  const [showExcelUpload, setShowExcelUpload] = useState(false);

  // Banka Mevduatı ekleme modalı
  const [showDepositForm, setShowDepositForm] = useState(false);

  // Mevduat eklenince portföye ekle
  const handleDepositAdded = (deposit: any) => {
    setPortfolio(prev => migrateDeposits([...prev, deposit]));
    setShowDepositForm(false);
  };
    // Portföy her güncellendiğinde migrate et
    useEffect(() => {
      setPortfolio(prev => migrateDeposits(prev));
    }, [portfolio.length]);
  
  // Sell transaction Excel upload modal visibility
  const [showSellExcelUpload, setShowSellExcelUpload] = useState(false);
  
  // Asset detail modal state
  const [selectedAsset, setSelectedAsset] = useState<PortfolioItem | null>(null);
  const [showAssetDetail, setShowAssetDetail] = useState(false);

  // Form verilerini localStorage'a kaydet
  useEffect(() => {
    localStorage.setItem('portfolio_newItem', JSON.stringify(newItem));
  }, [newItem]);

  useEffect(() => {
    localStorage.setItem('portfolio_sellItem', JSON.stringify(sellItem));
  }, [sellItem]);

  useEffect(() => {
    localStorage.setItem('portfolio_showAddForm', JSON.stringify(showAddForm));
  }, [showAddForm]);

  useEffect(() => {
    localStorage.setItem('portfolio_showSellForm', JSON.stringify(showSellForm));
  }, [showSellForm]);

  // Form temizleme fonksiyonu
  const clearAddForm = () => {
    const rate = getCurrentUSDRate();
    const emptyItem = {
      type: 'stock' as 'stock' | 'currency' | 'gold' | 'crypto',
      symbol: '',
      quantity: '',
      buyPrice: '',
      buyDate: new Date().toISOString().split('T')[0],
      usdRate: rate,
    };
    setNewItem(emptyItem);
    setAddUsdRate(rate);
    localStorage.setItem('portfolio_newItem', JSON.stringify(emptyItem));
  };

  const clearSellForm = () => {
    const rate = getCurrentUSDRate();
    const emptySellItem = {
      symbol: '',
      quantity: '',
      sellPrice: '',
      sellDate: new Date().toISOString().split('T')[0],
      usdRate: rate,
      buyPrice: 0,
      portfolioItemId: '', // Seçilen spesifik portföy item'ı
    };
    setSellItem(emptySellItem);
    setSellUsdRate(rate);
    localStorage.setItem('portfolio_sellItem', JSON.stringify(emptySellItem));
  };

  // Mevcut fiyatları güncelleyen useEffect
  useEffect(() => {
    if (portfolio.length === 0 || portfolioLoading) return;

    console.log('🔄 Updating portfolio prices...');
    console.log('📊 Stocks:', stocks.length, '💱 Currencies:', currencies.length, '🏅 Golds:', golds.length, '🪙 Cryptos:', cryptos.length);

    const updatedPortfolio = portfolio.map(item => {
      let currentPrice = item.buyPrice; // Varsayılan olarak alış fiyatını kullan
      let priceFound = false;

      switch (item.type) {
        case 'stock':
          const stock = stocks.find(s => s.StockCode === item.symbol);
          if (stock && stock.LastTransactionPrice) {
            currentPrice = stock.LastTransactionPrice;
            priceFound = true;
            console.log(`📈 Updated ${item.symbol}: ${item.currentPrice} → ${currentPrice}`);
          } else {
            console.log(`⚠️ Stock ${item.symbol} not found in stocks array`);
          }
          break;
        case 'currency':
          const currency = currencies.find(c => c.Code === item.symbol);
          if (currency && currency.BuyPrice) {
            currentPrice = currency.BuyPrice;
            priceFound = true;
            console.log(`💱 Updated ${item.symbol}: ${item.currentPrice} → ${currentPrice}`);
          } else {
            console.log(`⚠️ Currency ${item.symbol} not found in currencies array`);
          }
          break;
        case 'gold':
          const gold = golds.find(g => g.Type === item.symbol);
          if (gold && gold.Price) {
            currentPrice = gold.Price;
            priceFound = true;
            console.log(`🏅 Updated ${item.symbol}: ${item.currentPrice} → ${currentPrice}`);
          } else {
            console.log(`⚠️ Gold ${item.symbol} not found in golds array`);
          }
          break;
        case 'crypto':
          const crypto = cryptos.find(c => c.Symbol === item.symbol);
          if (crypto && crypto.Price) {
            currentPrice = crypto.Price;
            priceFound = true;
            console.log(`🪙 Updated ${item.symbol}: ${item.currentPrice} → ${currentPrice}`);
          } else {
            console.log(`⚠️ Crypto ${item.symbol} not found in cryptos array`);
          }
          break;
      }

      // Sadece fiyat değiştiyse hesaplamaları yap
      if (priceFound && Math.abs(currentPrice - item.currentPrice) > 0.01) {
        // Yeniden hesapla
        const totalValue = currentPrice * item.quantity;
        const profit = (currentPrice - item.buyPrice) * item.quantity;
        const profitPercent = item.buyPrice > 0 ? ((currentPrice - item.buyPrice) / item.buyPrice) * 100 : 0;

        // Dolar bazlı hesaplamalar
        const buyPriceUSD = item.buyPrice / item.usdRate;
        const currentPriceUSD = currentPrice / item.usdRate;
        const profitUSD = (currentPriceUSD - buyPriceUSD) * item.quantity;
        const profitPercentUSD = buyPriceUSD > 0 ? ((currentPriceUSD - buyPriceUSD) / buyPriceUSD) * 100 : 0;

        console.log(`💰 ${item.symbol} - New Total: ₺${totalValue.toFixed(2)}, Profit: ₺${profit.toFixed(2)}`);

        return {
          ...item,
          currentPrice,
          totalValue,
          profit,
          profitPercent,
          profitUSD,
          profitPercentUSD,
        };
      }

      return item; // Fiyat değişmediyse orijinal item'i döndür
    });

    // Değişiklik varsa güncelle
    const hasChanges = updatedPortfolio.some((updated, index) => {
      const original = portfolio[index];
      return updated.currentPrice !== original.currentPrice;
    });

    if (hasChanges) {
      console.log('✅ Portfolio prices updated');
      setPortfolio(updatedPortfolio);
    } else {
      console.log('ℹ️ No price changes detected');
    }
  }, [stocks, currencies, golds, cryptos, portfolio]); // portfolio.length ekledik

  // Portfolio yüklendikten sonra fiyatları güncellemek için ayrı useEffect
  useEffect(() => {
    if (portfolio.length > 0 && (stocks.length > 0 || currencies.length > 0 || golds.length > 0 || cryptos.length > 0)) {
      console.log('🔄 Portfolio loaded, triggering price update...');
      // Manuel olarak fiyat güncelleme tetikle
      setTimeout(() => {
        const event = new Event('portfolio-update');
        window.dispatchEvent(event);
      }, 100);
    }
  }, [portfolio.length, stocks.length, currencies.length, golds.length, cryptos.length]);

  // Güncel dolar kurunu currencies'dan al
  const getCurrentUSDRate = useCallback(() => {
    const usdCurrency = currencies.find(c => c.Code === 'USD');
    return usdCurrency ? usdCurrency.BuyPrice.toString() : '';
  }, [currencies]);

  // Form açıldığında güncel kuruyu ayarla (sadece form ilk açıldığında)
  const usdRateInitialized = React.useRef(false);
  useEffect(() => {
    if (showAddForm && currencies.length > 0 && !usdRateInitialized.current) {
      usdRateInitialized.current = true;
      setAddUsdRate(getCurrentUSDRate());
    }
    if (!showAddForm) {
      usdRateInitialized.current = false;
    }
  }, [showAddForm, currencies.length]);

  const [showSymbolSuggestions, setShowSymbolSuggestions] = useState(false);
  const [showSellSymbolSuggestions, setShowSellSymbolSuggestions] = useState(false);
  const [selectedType, setSelectedType] = useState<'all' | 'stock' | 'currency' | 'gold' | 'crypto' | 'deposit'>('all');
  const [selectedSellType, setSelectedSellType] = useState<'all' | 'stock' | 'currency' | 'gold' | 'crypto' | 'deposit'>('all');
  const [showSellHistory, setShowSellHistory] = useState(true);
  const [showAllocationChart, setShowAllocationChart] = useState(true);

  // Sync sell filter with portfolio filter
  useEffect(() => {
    setSelectedSellType(selectedType);
  }, [selectedType]);

  // Get symbol suggestions based on selected type
  const getSymbolSuggestions = () => {
    switch (newItem.type) {
      case 'stock':
        // BIST sayfasından gelen TÜM hisseleri kullan
        return stocks.map(stock => ({
          symbol: stock.StockCode,
          name: stock.StockCode,
          price: stock.LastTransactionPrice
        }));
      case 'currency':
        return currencies.map(currency => ({
          symbol: currency.Code,
          name: currency.Name,
          price: currency.BuyPrice
        }));
      case 'gold':
        return golds.map(gold => ({
          symbol: gold.Type,
          name: gold.Type,
          price: gold.Price
        }));
      case 'crypto':
        return cryptos.map(crypto => ({
          symbol: crypto.Symbol,
          name: crypto.Name,
          price: crypto.Price
        }));
      default:
        return [];
    }
  };

  const filteredSuggestions = getSymbolSuggestions().filter(item => 
    item.symbol.toLowerCase().includes(newItem.symbol.toLowerCase()) ||
    item.name.toLowerCase().includes(newItem.symbol.toLowerCase())
  ).slice(0, 50); // Performans için ilk 50 sonucu göster

  // Get sell symbol suggestions from portfolio
  const getSellSymbolSuggestions = () => {
    // Her bir portföy item'ını ayrı ayrı göster (gruplama yapma)
    return portfolio.map(item => ({
      id: item.id,
      symbol: item.symbol,
      name: item.name,
      quantity: item.quantity,
      buyPrice: item.buyPrice,
      buyDate: item.buyDate,
      type: item.type,
      totalCost: item.buyPrice * item.quantity,
      displayText: `${item.quantity} adet @ ₺${fmtTR(item.buyPrice)} (${item.buyDate})`
    }));
  };

  const filteredSellSuggestions = getSellSymbolSuggestions().filter(item => 
    item.symbol.toLowerCase().includes(sellItem.symbol.toLowerCase()) ||
    item.name.toLowerCase().includes(sellItem.symbol.toLowerCase())
  );

  // Filter sell transactions by selected type
  const filteredSellTransactions = selectedSellType === 'all' 
    ? sellTransactions 
    : sellTransactions.filter(transaction => transaction.type === selectedSellType);

  // Filter portfolio by selected type
  const filteredPortfolio = selectedType === 'all' 
    ? portfolio 
    : portfolio.filter(item => item.type === selectedType);

  // Get grouped and filtered portfolio
  const getGroupedFilteredPortfolio = () => {
    const grouped = groupPortfolioBySymbol();
    return selectedType === 'all' 
      ? grouped 
      : grouped.filter(item => item.type === selectedType);
  };

  // Calculate summary for filtered portfolio
  const calculateFilteredSummary = (): PortfolioSummary => {
    console.log('📊 Starting calculateFilteredSummary...');
    const totalValue = filteredPortfolio.reduce((sum, item) => sum + item.totalValue, 0);
    const totalCost = filteredPortfolio.reduce((sum, item) => sum + (item.buyPrice * item.quantity), 0);
    const totalProfit = totalValue - totalCost;
    
    // Maliyete göre yüzde hesabı: (Kar / Maliyet) * 100
    const totalProfitPercent = totalCost > 0 ? (totalProfit / totalCost) * 100 : 0;
    
    // Günlük kar/zarar ve yüzde değişim hesapla
    let totalDailyProfit = 0;
    let totalDailyProfitPercent = 0;
    let validItemsCount = 0;

    filteredPortfolio.forEach(item => {
      const dailyData = calculateDailyProfit(item);
      totalDailyProfit += dailyData.dailyProfit;
      
      // Yüzde değişim için ağırlıklı ortalama kullan
      if (item.buyPrice > 0 && item.quantity > 0) {
        const itemWeight = (item.buyPrice * item.quantity);
        totalDailyProfitPercent += dailyData.dailyProfitPercent * itemWeight;
        validItemsCount += itemWeight;
      }
    });

    // Ağırlıklı ortalama yüzde hesabı
    const weightedDailyProfitPercent = validItemsCount > 0 ? totalDailyProfitPercent / validItemsCount : 0;

    console.log('🔍 Filtered Summary - Cost:', totalCost, 'Value:', totalValue, 'Profit:', totalProfit, 'Percent:', totalProfitPercent);
    console.log('📈 Filtered Daily Profit:', totalDailyProfit, 'Daily Percent:', weightedDailyProfitPercent);

    return {
      totalValue,
      totalProfit,
      totalProfitPercent,
      itemsCount: filteredPortfolio.length,
      dailyProfit: totalDailyProfit,
      dailyProfitPercent: weightedDailyProfitPercent,
    };
  };

  // Group portfolio items by symbol
  const groupPortfolioBySymbol = () => {
    const grouped: { [key: string]: PortfolioItem[] } = {};
    
    portfolio.forEach(item => {
      const key = `${item.type}-${item.symbol}`;
      if (!grouped[key]) {
        grouped[key] = [];
      }
      grouped[key].push(item);
    });

    // Gruplanmış item'ları sembole göre alfabetik sırala
    return Object.entries(grouped)
      .sort(([keyA], [keyB]) => {
        // Sembol kısmını çıkar (type-symbol formatından)
        const symbolA = keyA.split('-')[1];
        const symbolB = keyB.split('-')[1];
        return symbolA.localeCompare(symbolB);
      })
      .map(([key, items]) => {
      let totalQuantity, totalCost, totalValue, avgBuyPrice, avgCurrentPrice, totalProfit, totalProfitPercent;
      let avgUsdRate, avgBuyPriceUSD, avgCurrentPriceUSD, totalProfitUSD, totalProfitPercentUSD;
      if (items[0].type === 'deposit') {
        // Mevduat için quantity = 1, buyPrice = ana para, currentPrice = ana para
        totalQuantity = 1;
        totalCost = items.reduce((sum, item) => sum + item.buyPrice, 0);
        totalValue = totalCost; // Mevduatta güncel değer = ana para
        avgBuyPrice = totalCost;
        avgCurrentPrice = totalValue;
        totalProfit = 0;
        totalProfitPercent = 0;
        avgUsdRate = items.reduce((sum, item) => sum + (item.usdRate || 1), 0) / items.length;
        avgBuyPriceUSD = avgBuyPrice / avgUsdRate;
        avgCurrentPriceUSD = avgCurrentPrice / avgUsdRate;
        totalProfitUSD = 0;
        totalProfitPercentUSD = 0;
      } else {
        totalQuantity = items.reduce((sum, item) => sum + item.quantity, 0);
        totalCost = items.reduce((sum, item) => sum + (item.buyPrice * item.quantity), 0);
        totalValue = items.reduce((sum, item) => sum + item.totalValue, 0);
        avgBuyPrice = totalCost / totalQuantity;
        avgCurrentPrice = totalValue / totalQuantity;
        totalProfit = totalValue - totalCost;
        totalProfitPercent = totalCost > 0 ? (totalProfit / totalCost) * 100 : 0;
        avgUsdRate = items.reduce((sum, item) => sum + item.usdRate, 0) / items.length;
        avgBuyPriceUSD = avgBuyPrice / avgUsdRate;
        avgCurrentPriceUSD = avgCurrentPrice / avgUsdRate;
        totalProfitUSD = (avgCurrentPriceUSD - avgBuyPriceUSD) * totalQuantity;
        totalProfitPercentUSD = avgBuyPriceUSD > 0 ? ((avgCurrentPriceUSD - avgBuyPriceUSD) / avgBuyPriceUSD) * 100 : 0;
      }

      return {
        id: items[0].id,
        type: items[0].type,
        symbol: items[0].symbol,
        name: items[0].name,
        quantity: totalQuantity,
        buyPrice: avgBuyPrice,
        currentPrice: avgCurrentPrice,
        totalValue,
        profit: totalProfit,
        profitPercent: totalProfitPercent,
        buyDate: items[0].buyDate,
        usdRate: avgUsdRate,
        profitUSD: totalProfitUSD,
        profitPercentUSD: totalProfitPercentUSD,
        items,
      };
    });
  };

  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
  const [editingItem, setEditingItem] = useState<PortfolioItem | null>(null);
  const [showEditForm, setShowEditForm] = useState(false);
  const [showEditSymbolSuggestions, setShowEditSymbolSuggestions] = useState(false);
  
  // Sell transaction editing states
  const [editingSellTransaction, setEditingSellTransaction] = useState<SellTransaction | null>(null);
  const [showSellEditForm, setShowSellEditForm] = useState(false);

  const toggleExpanded = (key: string) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(key)) {
      newExpanded.delete(key);
    } else {
      newExpanded.add(key);
    }
    setExpandedItems(newExpanded);
  };

  const handleEditItem = (item: PortfolioItem) => {
    setEditingItem(item);
    setShowEditForm(true);
    setShowEditSymbolSuggestions(false);
  };

  const handleUpdateItem = () => {
    if (!editingItem) return;

    const updatedPortfolio = portfolio.map(item => 
      item.id === editingItem.id ? editingItem : item
    );
    setPortfolio(updatedPortfolio);
    setShowEditForm(false);
    setEditingItem(null);
  };

  const handleCancelEdit = () => {
    setShowEditForm(false);
    setEditingItem(null);
    setShowEditSymbolSuggestions(false);
  };

  // Sell transaction editing functions
  const handleEditSellTransaction = (transaction: SellTransaction) => {
    setEditingSellTransaction(transaction);
    setShowSellEditForm(true);
  };

  const handleUpdateSellTransaction = () => {
    if (!editingSellTransaction) return;

    const updatedTransactions = sellTransactions.map(transaction => 
      transaction.id === editingSellTransaction.id ? editingSellTransaction : transaction
    );
    setSellTransactions(updatedTransactions);
    setShowSellEditForm(false);
    setEditingSellTransaction(null);
  };

  const handleCancelSellEdit = () => {
    setShowSellEditForm(false);
    setEditingSellTransaction(null);
  };

  const handleDeleteSellTransaction = (transactionId: string) => {
    if (window.confirm('Bu satış işlemini silmek istediğinizden emin misiniz?')) {
      const updatedTransactions = sellTransactions.filter(transaction => transaction.id !== transactionId);
      setSellTransactions(updatedTransactions);
    }
  };

  // Get symbol suggestions for edit form
  const getEditSymbolSuggestions = () => {
    if (!editingItem) return [];
    
    let suggestions: { symbol: string; name: string; price: number }[] = [];
    switch (editingItem.type) {
      case 'stock':
        suggestions = stocks.map(stock => ({
          symbol: stock.StockCode,
          name: stock.StockCode,
          price: stock.LastTransactionPrice
        }));
        break;
      case 'currency':
        suggestions = currencies.map(currency => ({
          symbol: currency.Code,
          name: currency.Name,
          price: currency.BuyPrice
        }));
        break;
      case 'gold':
        suggestions = golds.map(gold => ({
          symbol: gold.Type,
          name: gold.Type,
          price: gold.Price
        }));
        break;
      case 'crypto':
        suggestions = cryptos.map(crypto => ({
          symbol: crypto.Symbol,
          name: crypto.Name,
          price: crypto.Price
        }));
        break;
    }
    return suggestions;
  };

  const filteredEditSymbolSuggestions = getEditSymbolSuggestions().filter(item => {
    const searchTerm = editingItem?.symbol || '';
    return item.symbol.toLowerCase().includes(searchTerm.toLowerCase()) ||
           item.name.toLowerCase().includes(searchTerm.toLowerCase());
  });

  // Excel import handler
  const handleExcelImport = (items: PortfolioItem[]) => {
    setPortfolio(prev => [...prev, ...items]);
    setShowExcelUpload(false);
    console.log(`📊 ${items.length} items imported from Excel`);
  };

  // Sell transaction Excel import handler
  const handleSellExcelImport = (transactions: SellTransaction[]) => {
    setSellTransactions(prev => [...prev, ...transactions]);
    setShowSellExcelUpload(false);
    console.log(`💰 ${transactions.length} sell transactions imported from Excel`);
  };

  // Track if initial data load is complete
  const dataLoadedFromApi = useRef(false);
  const initialLoadComplete = useRef(false);

  // Load portfolio from API (with localStorage fallback) on mount
  useEffect(() => {
    if (!user?.id) return;
    if (initialLoadComplete.current) return; // Prevent double load

    const loadFromApi = async () => {
      setPortfolioLoading(true);
      try {
        const token = localStorage.getItem('token');
        if (!token) throw new Error('No token');

        const response = await fetch('/api/user-data', {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const data = await response.json();
          dataLoadedFromApi.current = true;

          if (data.portfolio && data.portfolio.length > 0) {
            setPortfolio(data.portfolio);
          }

          if (data.sellTransactions && data.sellTransactions.length > 0) {
            setSellTransactions(data.sellTransactions);
          }

          console.log('✅ Portfolio loaded from API:', data.portfolio?.length || 0, 'items');
          initialLoadComplete.current = true;
          setPortfolioLoading(false);
          return;
        }
        throw new Error('API error');
      } catch (error) {
        console.log('⚠️ API unavailable');
        initialLoadComplete.current = true;
        setPortfolioLoading(false);
      }
    };

    loadFromApi();
  }, [user?.id]);

  // Save portfolio to API whenever it changes
  useEffect(() => {
    if (!user?.id || !initialLoadComplete.current || portfolioLoading) return;
    const token = localStorage.getItem('token');
    if (token) {
      fetch('/api/user-data', {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ portfolio })
      }).catch(() => {});
    }
  }, [portfolio, user?.id, portfolioLoading]);

  // Save sell transactions to API whenever they change
  useEffect(() => {
    if (!user?.id || !initialLoadComplete.current || portfolioLoading) return;
    const token = localStorage.getItem('token');
    if (token) {
      fetch('/api/user-data', {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ sellTransactions })
      }).catch(() => {});
    }
  }, [sellTransactions, user?.id, portfolioLoading]);

  // Update current prices and calculate profits (without changing portfolio state)
  useEffect(() => {
    // Sadece fiyatları güncellemek için, state'i değiştirmiyoruz
    // Bu useEffect sadece referans tutmak için, state değişimi handleDelete/Add'de yapılıyor
  }, [stocks, currencies, golds, cryptos]);

  // Satış kar/zarar hesapla
  const calculateSellProfit = (): number => {
    if (!sellItem.quantity || !sellItem.sellPrice || !sellItem.buyPrice) return 0;
    const quantity = parseFloat(sellItem.quantity);
    const sellPrice = parseFloat(sellItem.sellPrice);
    const buyPrice = parseFloat(sellItem.buyPrice);
    return (sellPrice - buyPrice) * quantity;
  };

  const calculateSellProfitPercent = (): number => {
    if (!sellItem.quantity || !sellItem.sellPrice || !sellItem.buyPrice) return 0;
    const buyPrice = parseFloat(sellItem.buyPrice);
    const sellPrice = parseFloat(sellItem.sellPrice);
    return buyPrice > 0 ? ((sellPrice - buyPrice) / buyPrice) * 100 : 0;
  };

  // Günlük kar/zarar hesapla (Tüm varlık türleri için)
  const calculateDailyProfit = (item: PortfolioItem): { dailyProfit: number; dailyProfitPercent: number } => {
    // Banka Mevduatı için eski formatı fonksiyon içinde düzelt
    let fixedItem = item;
    if (item.type === 'deposit' && item.quantity > 1 && (item.buyPrice === 1 || item.currentPrice === 1)) {
      fixedItem = {
        ...item,
        buyPrice: item.quantity,
        currentPrice: item.quantity,
        quantity: 1,
      };
    }
    console.log(`🔍 Calculating daily profit for ${fixedItem.symbol} (${fixedItem.type}):`);
    console.log(`  - Current Price: ₺${fixedItem.currentPrice}`);
    console.log(`  - Buy Price: ₺${fixedItem.buyPrice}`);
    console.log(`  - Quantity: ${fixedItem.quantity}`);
    
    if (fixedItem.type === 'stock') {
      // BIST verilerinden günlük değişimi bul
      const bistData = stocks.find(stock => stock.StockCode === item.symbol);
      console.log(`  - BIST Data:`, bistData);
      if (bistData && bistData.Change !== undefined && bistData.ChangePercent !== undefined) {
        const dailyProfit = bistData.Change * item.quantity;
        const dailyProfitPercent = bistData.ChangePercent;
        console.log(`  - BIST Daily Profit: ₺${dailyProfit} (${dailyProfitPercent}%)`);
        return { dailyProfit, dailyProfitPercent };
      }
    } else if (item.type === 'currency') {
      // Döviz verilerinden günlük değişimi bul
      const currencyData = currencies.find(curr => curr.Code === item.symbol);
      console.log(`  - Currency Data for ${item.symbol}:`, currencyData);
      console.log(`  - Available currencies:`, currencies.map(c => ({ Code: c.Code, hasChange: c.Change !== undefined, hasChangePercent: c.ChangePercent !== undefined })));
      if (currencyData && currencyData.Change !== undefined && currencyData.ChangePercent !== undefined) {
        const dailyProfit = currencyData.Change * item.quantity;
        const dailyProfitPercent = currencyData.ChangePercent;
        console.log(`  - Currency Daily Profit: ₺${dailyProfit} (${dailyProfitPercent}%)`);
        return { dailyProfit, dailyProfitPercent };
      } else {
        console.log(`  - Currency ${item.symbol} missing change data. Available fields:`, currencyData ? Object.keys(currencyData) : 'Not found');
      }
    } else if (item.type === 'gold') {
      // Altın verilerinden günlük değişimi bul
      const goldData = golds.find(gold => gold.Type === item.symbol);
      console.log(`  - Gold Data for ${item.symbol}:`, goldData);
      console.log(`  - Available golds:`, golds.map(g => ({ Type: g.Type, hasChange: g.Change !== undefined, hasChangePercent: g.ChangePercent !== undefined })));
      if (goldData && goldData.Change !== undefined && goldData.ChangePercent !== undefined) {
        const dailyProfit = goldData.Change * item.quantity;
        const dailyProfitPercent = goldData.ChangePercent;
        console.log(`  - Gold Daily Profit: ₺${dailyProfit} (${dailyProfitPercent}%)`);
        return { dailyProfit, dailyProfitPercent };
      } else {
        console.log(`  - Gold ${item.symbol} missing change data. Available fields:`, goldData ? Object.keys(goldData) : 'Not found');
      }
    } else if (item.type === 'crypto') {
      // Kripto verilerinden günlük değişimi bul
      const cryptoData = cryptos.find(crypto => crypto.Symbol === item.symbol);
      console.log(`  - Crypto Data:`, cryptoData);
      if (cryptoData && cryptoData.Change !== undefined && cryptoData.ChangePercent !== undefined) {
        const dailyProfit = cryptoData.Change * item.quantity;
        const dailyProfitPercent = cryptoData.ChangePercent;
        console.log(`  - Crypto Daily Profit: ₺${dailyProfit} (${dailyProfitPercent}%)`);
        return { dailyProfit, dailyProfitPercent };
      }
    }
    
    // Veri bulunamayanlar için basit yaklaşım (fiyat farkı)
    const priceChange = fixedItem.currentPrice - fixedItem.buyPrice;
    const dailyProfit = priceChange * fixedItem.quantity;
    const dailyProfitPercent = fixedItem.buyPrice > 0 ? (priceChange / fixedItem.buyPrice) * 100 : 0;
    console.log(`  - Fallback Daily Profit: ₺${dailyProfit} (${dailyProfitPercent}%)`);
    
    return { dailyProfit, dailyProfitPercent };
  };

  // Portföydeki tüm varlıkların günlük kar/zarar ve yüzde değişim toplamlarını hesapla
  const calculateDailyTotals = (): { totalDailyProfit: number; totalDailyProfitPercent: number } => {
    console.log('📊 Starting calculateDailyTotals...');
    console.log(`  - Portfolio items: ${portfolio.length}`);
    console.log(`  - Stocks available: ${stocks.length}`);
    
    let totalDailyProfit = 0;
    let totalDailyProfitPercent = 0;
    let validItemsCount = 0;

    portfolio.forEach(item => {
      console.log(`\n  Processing item: ${item.symbol} (${item.type})`);
      const dailyData = calculateDailyProfit(item);
      totalDailyProfit += dailyData.dailyProfit;
      
      console.log(`    - Daily Profit: ₺${dailyData.dailyProfit}`);
      console.log(`    - Daily Percent: ${dailyData.dailyProfitPercent}%`);
      
      // Yüzde değişim için ağırlıklı ortalama kullan
      if (item.buyPrice > 0 && item.quantity > 0) {
        const itemWeight = (item.buyPrice * item.quantity);
        totalDailyProfitPercent += dailyData.dailyProfitPercent * itemWeight;
        validItemsCount += itemWeight;
        console.log(`    - Item Weight: ₺${itemWeight}`);
      }
    });

    // Ağırlıklı ortalama yüzde hesabı
    const weightedDailyProfitPercent = validItemsCount > 0 ? totalDailyProfitPercent / validItemsCount : 0;

    console.log('\n📊 Daily Totals Calculation Results:');
    console.log(`💰 Total Daily Profit: ₺${totalDailyProfit.toFixed(2)}`);
    console.log(`📈 Weighted Daily Percent: ${fmtTR(weightedDailyProfitPercent)}%`);
    console.log(`📊 Valid Items Weight: ₺${validItemsCount.toFixed(2)}`);

    return {
      totalDailyProfit,
      totalDailyProfitPercent: weightedDailyProfitPercent
    };
  };

  const calculateSummary = (): PortfolioSummary => {
    console.log('📊 Starting calculateSummary...');
    const totalValue = portfolio.reduce((sum, item) => sum + item.totalValue, 0);
    const totalCost = portfolio.reduce((sum, item) => sum + (item.buyPrice * item.quantity), 0);
    const unrealizedProfit = totalValue - totalCost; // Aktif portföyün kar/zararı
    
    // Satışlardan realize edilmiş toplam kar/zarar (Toplam Kar/Zarar'a EKLEME)
    const totalRealizedProfit = sellTransactions.reduce((sum, transaction) => sum + transaction.realizedProfit, 0);
    
    // Toplam kar/zarar = Sadece AKTİF portföyün kar/zararı (satışları dahil etme)
    const totalProfit = unrealizedProfit;
    
    // Maliyete göre yüzde hesabı: (Toplam Kar / Maliyet) * 100
    const totalProfitPercent = totalCost > 0 ? (totalProfit / totalCost) * 100 : 0;
    
    // Günlük kar/zarar ve yüzde değişim hesapla
    const dailyTotals = calculateDailyTotals();
    
    // Debug log
    console.log('📊 Portfolio Summary Calculation (AKTİF PORTFÖY ONLY):');
    console.log(`💰 Total Cost: ₺${totalCost.toFixed(2)}`);
    console.log(`💎 Total Value: ₺${totalValue.toFixed(2)}`);
    console.log(`📈 Unrealized Profit: ₺${unrealizedProfit.toFixed(2)}`);
    console.log(`💰 Realized Profit (Satışlar): ₺${totalRealizedProfit.toFixed(2)}`);
    console.log(`📊 Total Profit (Aktif Only): ₺${totalProfit.toFixed(2)}`);
    console.log(`📊 Profit % (Cost-based): ${fmtTR(totalProfitPercent)}%`);
    console.log(`📈 Daily Profit: ₺${dailyTotals.totalDailyProfit.toFixed(2)}`);
    console.log(`📈 Daily Percent: ${fmtTR(dailyTotals.totalDailyProfitPercent)}%`);

    return {
      totalValue,
      totalProfit,
      totalProfitPercent,
      itemsCount: portfolio.length,
      realizedProfit: totalRealizedProfit,
      unrealizedProfit: totalProfit,
      dailyProfit: dailyTotals.totalDailyProfit,
      dailyProfitPercent: dailyTotals.totalDailyProfitPercent,
    };
  };

  const handleAddItem = () => {
    const usdRateValue = addUsdRate.replace(',', '.');
    if (!newItem.symbol || !newItem.quantity || !newItem.buyPrice || !usdRateValue) {
      alert('Lütfen tüm alanları doğru şekilde doldurun (dolar kurunu da girin)');
      return;
    }

    const quantity = parseFloat(newItem.quantity);
    const buyPrice = parseFloat(newItem.buyPrice);
    const usdRate = parseFloat(usdRateValue);

    if (quantity <= 0 || buyPrice <= 0 || usdRate <= 0) {
      alert('Lütfen tüm alanları doğru şekilde doldurun (dolar kurunu da girin)');
      return;
    }

    let name = newItem.symbol;
    let currentPrice = buyPrice;

    switch (newItem.type) {
      case 'stock':
        const stock = stocks.find(s => s.StockCode === newItem.symbol);
        if (stock) {
          name = stock.StockCode;
          currentPrice = stock.LastTransactionPrice;
        }
        break;
      case 'currency':
        const currency = currencies.find(c => c.Code === newItem.symbol);
        if (currency) {
          name = currency.Name;
          currentPrice = currency.BuyPrice;
        }
        break;
      case 'gold':
        const gold = golds.find(g => g.Type === newItem.symbol);
        if (gold) {
          name = gold.Type;
          currentPrice = gold.Price;
        }
        break;
      case 'crypto':
        const crypto = cryptos.find(c => c.Symbol === newItem.symbol);
        if (crypto) {
          name = crypto.Name;
          currentPrice = crypto.Price;
        }
        break;
    }

    // TL bazlı kar/zarar hesaplaması
    const totalValue = currentPrice * quantity;
    const profit = (currentPrice - buyPrice) * quantity;
    const profitPercent = ((currentPrice - buyPrice) / buyPrice) * 100;

    // Dolar bazlı kar/zarar hesaplaması
    const buyPriceUSD = buyPrice / usdRate;
    const currentPriceUSD = currentPrice / usdRate;
    const profitUSD = (currentPriceUSD - buyPriceUSD) * quantity;
    const profitPercentUSD = ((currentPriceUSD - buyPriceUSD) / buyPriceUSD) * 100;

    const portfolioItem: PortfolioItem = {
      id: Date.now().toString(),
      type: newItem.type,
      symbol: newItem.symbol,
      name,
      quantity,
      buyPrice,
      currentPrice,
      totalValue,
      profit,
      profitPercent,
      buyDate: newItem.buyDate,
      usdRate,
      profitUSD,
      profitPercentUSD,
    };

    setPortfolio([...portfolio, portfolioItem]);
    clearAddForm();
    setShowAddForm(false);
  };

  const handleDeleteItem = (id: string) => {
    setPortfolio(portfolio.filter(item => item.id !== id));
  };

  const handleSellItem = () => {
    const sellUsdRateValue = sellUsdRate.replace(',', '.');
    if (!sellItem.symbol || !sellItem.quantity || !sellItem.sellPrice || !sellUsdRateValue || !sellItem.portfolioItemId) {
      alert('Lütfen tüm alanları doğru şekilde doldurun ve spesifik bir alım seçin');
      return;
    }

    const quantity = parseFloat(sellItem.quantity);
    const sellPrice = parseFloat(sellItem.sellPrice);
    const usdRate = parseFloat(sellUsdRateValue);

    if (quantity <= 0 || sellPrice <= 0 || usdRate <= 0) {
      alert('Lütfen tüm alanları doğru şekilde doldurun');
      return;
    }

    // Seçilen spesifik portföy item'ını bul
    const selectedItem = portfolio.find(item => item.id === sellItem.portfolioItemId);
    if (!selectedItem) {
      alert('Seçilen alım bulunamadı');
      return;
    }

    if (quantity > selectedItem.quantity) {
      alert(`Satış miktarı seçilen alım miktarından fazla olamaz. Mevcut: ${selectedItem.quantity}`);
      return;
    }

    // Seçilen item'ın alış fiyatını kullan
    const buyPrice = selectedItem.buyPrice;

    // Realize edilmiş kar/zarar hesapla
    const realizedProfit = (sellPrice - buyPrice) * quantity;
    const realizedProfitPercent = ((sellPrice - buyPrice) / buyPrice) * 100;

    // Dolar bazlı hesaplamalar
    const buyPriceUSD = buyPrice / usdRate;
    const sellPriceUSD = sellPrice / usdRate;
    const realizedProfitUSD = (sellPriceUSD - buyPriceUSD) * quantity;
    const realizedProfitPercentUSD = ((sellPriceUSD - buyPriceUSD) / buyPriceUSD) * 100;

    // Satış işlemi kaydı oluştur
    const sellTransaction: SellTransaction = {
      id: Date.now().toString(),
      type: selectedItem.type,
      symbol: sellItem.symbol,
      name: selectedItem.name,
      quantity,
      sellPrice,
      buyPrice, // Seçilen spesifik alış fiyatı
      realizedProfit,
      realizedProfitPercent,
      sellDate: sellItem.sellDate,
      usdRate,
      realizedProfitUSD,
      realizedProfitPercentUSD,
    };

    // Portföyü güncelle - sadece seçilen item'ı güncelle
    const updatedPortfolio = portfolio.map(item => {
      if (item.id === sellItem.portfolioItemId) {
        if (quantity === item.quantity) {
          // Tamamen sat
          return {
            ...item,
            quantity: 0,
            totalValue: 0,
            profit: 0,
            profitPercent: 0,
          };
        } else {
          // Kısmen sat
          const newQuantity = item.quantity - quantity;
          const newTotalValue = item.currentPrice * newQuantity;
          const newProfit = (item.currentPrice - item.buyPrice) * newQuantity;
          const newProfitPercent = ((item.currentPrice - item.buyPrice) / item.buyPrice) * 100;
          
          return {
            ...item,
            quantity: newQuantity,
            totalValue: newTotalValue,
            profit: newProfit,
            profitPercent: newProfitPercent,
          };
        }
      }
      return item;
    }).filter(item => item.quantity > 0); // Sıfır miktarlı item'ları temizle

    setPortfolio(updatedPortfolio);
    setSellTransactions([...sellTransactions, sellTransaction]);
    
    // Formu temizle
    clearSellForm();
    setShowSellForm(false);

    console.log(`💰 Spesifik satış işlemi: ${sellItem.symbol} - ${quantity} adet @ ₺${sellPrice} (Alış: ₺${buyPrice}) - Kar/Zarar: ₺${realizedProfit.toFixed(2)}`);
  };

  const handleSellSpecificItem = (portfolioItem: PortfolioItem) => {
    // Satış formunu doldur ve göster
    setSellItem({
      symbol: portfolioItem.symbol,
      quantity: portfolioItem.quantity.toString(),
      sellPrice: portfolioItem.currentPrice.toString(),
      sellDate: new Date().toISOString().split('T')[0],
      usdRate: portfolioItem.usdRate.toString(),
      buyPrice: portfolioItem.buyPrice,
      portfolioItemId: portfolioItem.id, // Spesifik item'ı sakla
    });
    setShowSellForm(true);
    console.log(`📋 Preparing sell for: ${portfolioItem.symbol} - ${portfolioItem.quantity} units at ₺${portfolioItem.currentPrice} (Buy: ₺${portfolioItem.buyPrice})`);
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'stock': return '📈';
      case 'currency': return '💱';
      case 'gold': return '🏅';
      case 'crypto': return '🪙';
      default: return '📊';
    }
  };

  // Handle asset click for detail modal
  const handleAssetClick = (asset: PortfolioItem) => {
    setSelectedAsset(asset);
    setShowAssetDetail(true);
  };

  const getTypeName = (type: string) => {
    switch (type) {
      case 'stock': return 'Hisse';
      case 'currency': return 'Döviz';
      case 'gold': return 'Altın';
      case 'crypto': return 'Kripto';
      default: return 'Diğer';
    }
  };

  const summary = calculateFilteredSummary();

  // Calculate stock-specific metrics
  const calculateStockMetrics = () => {
    const stockPortfolio = portfolio.filter(item => item.type === 'stock');
    const stockTotalValue = stockPortfolio.reduce((sum, item) => sum + item.totalValue, 0);
    const stockTotalCost = stockPortfolio.reduce((sum, item) => sum + (item.buyPrice * item.quantity), 0);
    const stockProfit = stockTotalValue - stockTotalCost;
    
    // Calculate total stock sell profit from transactions
    const stockSellProfit = sellTransactions
      .filter(transaction => transaction.type === 'stock')
      .reduce((sum, transaction) => sum + transaction.realizedProfit, 0);
    
    // Maliyet = Hisse Toplam Değer - Hisse Kar/Zarar - Satış Toplam Kar/Zarar
    const maliyet = stockTotalValue - stockProfit - stockSellProfit;
    
    // % Kar Zarar = (Toplam Kar / Maliyet) * 100
    const totalProfit = stockProfit + stockSellProfit;
    const stockProfitPercent = maliyet > 0 ? (totalProfit / maliyet) * 100 : 0;
    
    return {
      stockTotalValue,
      stockProfit,
      stockProfitPercent,
      stockSellProfit,
      maliyet,
      totalProfit
    };
  };

  const stockMetrics = calculateStockMetrics();

  // Calculate currency-specific metrics
  const calculateCurrencyMetrics = () => {
    const currencyPortfolio = portfolio.filter(item => item.type === 'currency');
    const currencyTotalValue = currencyPortfolio.reduce((sum, item) => sum + item.totalValue, 0);
    const currencyTotalCost = currencyPortfolio.reduce((sum, item) => sum + (item.buyPrice * item.quantity), 0);
    const currencyProfit = currencyTotalValue - currencyTotalCost;
    
    // Calculate total currency sell profit from transactions
    const currencySellProfit = sellTransactions
      .filter(transaction => transaction.type === 'currency')
      .reduce((sum, transaction) => sum + transaction.realizedProfit, 0);
    
    // Maliyet = Döviz Toplam Değer - Döviz Kar/Zarar - Satış Toplam Kar/Zarar
    const maliyet = currencyTotalValue - currencyProfit - currencySellProfit;
    
    // % Kar Zarar = (Toplam Kar / Maliyet) * 100
    const totalProfit = currencyProfit + currencySellProfit;
    const currencyProfitPercent = maliyet > 0 ? (totalProfit / maliyet) * 100 : 0;
    
    console.log('💱 Currency Metrics Debug:');
    console.log(`  - Total Value: ₺${currencyTotalValue.toFixed(2)}`);
    console.log(`  - Total Cost: ₺${currencyTotalCost.toFixed(2)}`);
    console.log(`  - Active Profit: ₺${currencyProfit.toFixed(2)}`);
    console.log(`  - Sell Profit: ₺${currencySellProfit.toFixed(2)}`);
    console.log(`  - Total Profit: ₺${totalProfit.toFixed(2)}`);
    console.log(`  - Maliyet: ₺${maliyet.toFixed(2)}`);
    console.log(`  - Profit %: ${fmtTR(currencyProfitPercent)}%`);
    
    return {
      currencyTotalValue,
      currencyProfit,
      currencyProfitPercent,
      currencySellProfit,
      maliyet,
      totalProfit
    };
  };

  // Calculate gold-specific metrics
  const calculateGoldMetrics = () => {
    const goldPortfolio = portfolio.filter(item => item.type === 'gold');
    const goldTotalValue = goldPortfolio.reduce((sum, item) => sum + item.totalValue, 0);
    const goldTotalCost = goldPortfolio.reduce((sum, item) => sum + (item.buyPrice * item.quantity), 0);
    const goldProfit = goldTotalValue - goldTotalCost;
    
    // Calculate total gold sell profit from transactions
    const goldSellProfit = sellTransactions
      .filter(transaction => transaction.type === 'gold')
      .reduce((sum, transaction) => sum + transaction.realizedProfit, 0);
    
    // Maliyet = Altın Toplam Değer - Altın Kar/Zarar - Satış Toplam Kar/Zarar
    const maliyet = goldTotalValue - goldProfit - goldSellProfit;
    
    // % Kar Zarar = (Toplam Kar / Maliyet) * 100
    const totalProfit = goldProfit + goldSellProfit;
    const goldProfitPercent = maliyet > 0 ? (totalProfit / maliyet) * 100 : 0;
    
    console.log('🏅 Gold Metrics Debug:');
    console.log(`  - Total Value: ₺${goldTotalValue.toFixed(2)}`);
    console.log(`  - Total Cost: ₺${goldTotalCost.toFixed(2)}`);
    console.log(`  - Active Profit: ₺${goldProfit.toFixed(2)}`);
    console.log(`  - Sell Profit: ₺${goldSellProfit.toFixed(2)}`);
    console.log(`  - Total Profit: ₺${totalProfit.toFixed(2)}`);
    console.log(`  - Maliyet: ₺${maliyet.toFixed(2)}`);
    console.log(`  - Profit %: ${fmtTR(goldProfitPercent)}%`);
    
    return {
      goldTotalValue,
      goldProfit,
      goldProfitPercent,
      goldSellProfit,
      maliyet,
      totalProfit
    };
  };

  // Calculate crypto-specific metrics
  const calculateCryptoMetrics = () => {
    const cryptoPortfolio = portfolio.filter(item => item.type === 'crypto');
    const cryptoTotalValue = cryptoPortfolio.reduce((sum, item) => sum + item.totalValue, 0);
    const cryptoTotalCost = cryptoPortfolio.reduce((sum, item) => sum + (item.buyPrice * item.quantity), 0);
    const cryptoProfit = cryptoTotalValue - cryptoTotalCost;
    
    // Calculate total crypto sell profit from transactions
    const cryptoSellProfit = sellTransactions
      .filter(transaction => transaction.type === 'crypto')
      .reduce((sum, transaction) => sum + transaction.realizedProfit, 0);
    
    // Maliyet = Kripto Toplam Değer - Kripto Kar/Zarar - Satış Toplam Kar/Zarar
    const maliyet = cryptoTotalValue - cryptoProfit - cryptoSellProfit;
    
    // % Kar Zarar = (Toplam Kar / Maliyet) * 100
    const totalProfit = cryptoProfit + cryptoSellProfit;
    const cryptoProfitPercent = maliyet > 0 ? (totalProfit / maliyet) * 100 : 0;
    
    console.log('🪙 Crypto Metrics Debug:');
    console.log(`  - Total Value: ₺${cryptoTotalValue.toFixed(2)}`);
    console.log(`  - Total Cost: ₺${cryptoTotalCost.toFixed(2)}`);
    console.log(`  - Active Profit: ₺${cryptoProfit.toFixed(2)}`);
    console.log(`  - Sell Profit: ₺${cryptoSellProfit.toFixed(2)}`);
    console.log(`  - Total Profit: ₺${totalProfit.toFixed(2)}`);
    console.log(`  - Maliyet: ₺${maliyet.toFixed(2)}`);
    console.log(`  - Profit %: ${fmtTR(cryptoProfitPercent)}%`);
    
    return {
      cryptoTotalValue,
      cryptoProfit,
      cryptoProfitPercent,
      cryptoSellProfit,
      maliyet,
      totalProfit
    };
  };

  const currencyMetrics = calculateCurrencyMetrics();
  const goldMetrics = calculateGoldMetrics();
  const cryptoMetrics = calculateCryptoMetrics();

  const exportPortfolioCSV = () => {
    if (portfolio.length === 0) return;
    const typeLabel: Record<string, string> = { stock: 'Hisse', currency: 'Döviz', gold: 'Altın', crypto: 'Kripto' };
    const rows = portfolio.map(item => ({
      Tür: typeLabel[item.type] || item.type,
      Sembol: item.symbol,
      Adet: item.type === 'deposit' ? item.buyPrice : item.quantity,
      'Alış Fiyatı': item.buyPrice,
      'Güncel Fiyat': item.currentPrice,
      'Toplam Değer': item.totalValue || (item.currentPrice * item.quantity),
      'Kâr/Zarar': (item.currentPrice - item.buyPrice) * item.quantity,
      'Alış Tarihi': item.buyDate || '',
    }));
    const header = Object.keys(rows[0]).join(';');
    const csv = [header, ...rows.map(r => Object.values(r).join(';'))].join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `portfoy_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Debug için satış işlemlerini logla
  console.log('📊 Sell Transactions Debug:');
  console.log(`📝 Total sell transactions: ${sellTransactions.length}`);
  console.log('💰 Transactions:', sellTransactions);

  if (portfolioLoading && portfolio.length === 0) {
    return (
      <div className="portfolio-container">
        <div style={{ textAlign: 'center', padding: '40px 20px' }}>
          <div className="loading-spinner"></div>
          <p>Portföy yükleniyor...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="portfolio-container">
      <div className="portfolio-header">
        <h2>💼 Portföyüm</h2>
        <div className="header-buttons">
          <button 
            className="excel-button"
            onClick={() => setShowExcelUpload(true)}
          >
            📊 Excel Import
          </button>
          <button
            className="export-btn"
            onClick={exportPortfolioCSV}
            title="CSV İndir"
          >
            📥 CSV
          </button>
          <button 
            className="sell-button"
            onClick={() => setShowSellForm(!showSellForm)}
          >
            💰 Satış Yap
          </button>
          <button 
            className="add-button"
            onClick={() => setShowAddForm(!showAddForm)}
          >
            + Yeni Ekle
          </button>
          <button
            className="add-button"
            style={{background: '#06b6d4', color: '#fff'}} 
            onClick={() => setShowDepositForm(true)}
          >
            🏦 Banka Mevduatı Ekle
          </button>
        </div>
      </div>

      {/* Portfolio Summary */}
      <div className="portfolio-summary">
        <div className="summary-card">
          <h3>Günlük Kar/Zarar TL Bazlı (₺)</h3>
          <p className="summary-value">₺{summary.dailyProfit?.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0,00'}</p>
        </div>
        <div className="summary-card">
          <h3>Günlük % Değişim TL Bazlı (%)</h3>
          <p className={`summary-value ${(summary.dailyProfitPercent ?? 0) >= 0 ? 'positive' : 'negative'}`}>
            {(summary.dailyProfitPercent ?? 0) >= 0 ? '+' : ''}{fmtTR(summary.dailyProfitPercent) || '0,00'}%
          </p>
        </div>
        <div className="summary-card">
          <h3>Varlık Sayısı</h3>
          <p className="summary-value">{summary.itemsCount}</p>
        </div>
      </div>

      {/* Stock-specific Summary - Only show when stock filter is selected */}
      {selectedType === 'stock' && (
        <div className="portfolio-summary stock-summary">
          <div className="summary-card">
            <h3>📈 Hisse Toplam Değer</h3>
            <p className="summary-value">₺{stockMetrics.stockTotalValue.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
          </div>
          <div className="summary-card">
            <h3>Hisse Kar/Zarar</h3>
            <p className={`summary-value ${stockMetrics.stockProfit >= 0 ? 'positive' : 'negative'}`}>
              ₺{stockMetrics.stockProfit.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </p>
          </div>
          <div className="summary-card">
            <h3>% Kar Zarar</h3>
            <p className={`summary-value ${stockMetrics.stockProfitPercent >= 0 ? 'positive' : 'negative'}`}>
              {stockMetrics.stockProfitPercent >= 0 ? '+' : ''}{fmtTR(stockMetrics.stockProfitPercent)}%
            </p>
          </div>
          <div className="summary-card">
            <h3>Maliyet</h3>
            <p className="summary-value">₺{stockMetrics.maliyet.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
          </div>
        </div>
      )}

      {/* Currency-specific Summary - Only show when currency filter is selected */}
      {selectedType === 'currency' && (
        <div className="portfolio-summary currency-summary">
          <div className="summary-card">
            <h3>💱 Döviz Toplam Değer</h3>
            <p className="summary-value">₺{currencyMetrics.currencyTotalValue.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
          </div>
          <div className="summary-card">
            <h3>Döviz Kar/Zarar</h3>
            <p className={`summary-value ${currencyMetrics.currencyProfit >= 0 ? 'positive' : 'negative'}`}>
              ₺{currencyMetrics.currencyProfit.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </p>
          </div>
          <div className="summary-card">
            <h3>% Kar Zarar</h3>
            <p className={`summary-value ${currencyMetrics.currencyProfitPercent >= 0 ? 'positive' : 'negative'}`}>
              {currencyMetrics.currencyProfitPercent >= 0 ? '+' : ''}{fmtTR(currencyMetrics.currencyProfitPercent)}%
            </p>
          </div>
          <div className="summary-card">
            <h3>Maliyet</h3>
            <p className="summary-value">₺{currencyMetrics.maliyet.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
          </div>
        </div>
      )}

      {/* Gold-specific Summary - Only show when gold filter is selected */}
      {selectedType === 'gold' && (
        <div className="portfolio-summary gold-summary">
          <div className="summary-card">
            <h3>🏅 Altın Toplam Değer</h3>
            <p className="summary-value">₺{goldMetrics.goldTotalValue.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
          </div>
          <div className="summary-card">
            <h3>Altın Kar/Zarar</h3>
            <p className={`summary-value ${goldMetrics.goldProfit >= 0 ? 'positive' : 'negative'}`}>
              ₺{goldMetrics.goldProfit.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </p>
          </div>
          <div className="summary-card">
            <h3>% Kar Zarar</h3>
            <p className={`summary-value ${goldMetrics.goldProfitPercent >= 0 ? 'positive' : 'negative'}`}>
              {goldMetrics.goldProfitPercent >= 0 ? '+' : ''}{fmtTR(goldMetrics.goldProfitPercent)}%
            </p>
          </div>
          <div className="summary-card">
            <h3>Maliyet</h3>
            <p className="summary-value">₺{goldMetrics.maliyet.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
          </div>
        </div>
      )}


      {/* Deposit-specific Summary - Only show when deposit filter is selected */}
      {selectedType === 'deposit' && (
        <div className="portfolio-summary deposit-summary">
          {portfolio.filter(item => item.type === 'deposit').map((item, idx) => {
            // Mevduat için faiz ve vade sonu hesaplama
            // buyPrice: ana para, quantity: 1, symbol: banka adı, name: banka adı
            // currentPrice: ana para (güncel değer yok)
            // Ek alanlar: interestRate, maturityDay, withholdingTax
            // Not: Eski kayıtlarda bu alanlar olmayabilir, fallback'liyoruz
            const amount = item.buyPrice;
            const interestRate = (item as any).interestRate ?? 0;
            const maturityDay = (item as any).maturityDay ?? 0;
            const withholdingTax = (item as any).withholdingTax ?? 0;
            const grossInterest = amount * (interestRate / 100) * (maturityDay / 365);
            const netInterest = grossInterest * (1 - (withholdingTax / 100));
            const totalAtMaturity = amount + netInterest;
            return (
              <div className="summary-card" key={item.id} style={{minWidth: 260, marginBottom: 16}}>
                <h3 style={{marginBottom: 8}}>{item.name || item.symbol} Mevduatı</h3>
                <div style={{fontSize: 15, color: '#888', marginBottom: 4}}>Ana Para: ₺{amount.toLocaleString('tr-TR', {minimumFractionDigits:2, maximumFractionDigits:2})}</div>
                <div style={{fontWeight: 600, fontSize: 16, marginBottom: 4}}>Vade Sonu Tutarı</div>
                <div style={{fontSize: 22, fontWeight: 700, color: '#06b6d4', marginBottom: 4}}>₺{totalAtMaturity.toLocaleString('tr-TR', {minimumFractionDigits:2, maximumFractionDigits:2})}</div>
                <div style={{fontSize: 14, color: '#888'}}>Net faiz kazancı: ₺{netInterest.toLocaleString('tr-TR', {minimumFractionDigits:2, maximumFractionDigits:2})}</div>
                <div style={{fontSize: 13, color: '#aaa', marginTop: 4}}>Faiz: %{interestRate} | Vade: {maturityDay} gün | Stopaj: %{withholdingTax}</div>
              </div>
            );
          })}
        </div>
      )}

      {/* Crypto-specific Summary - Only show when crypto filter is selected */}
      {selectedType === 'crypto' && (
        <div className="portfolio-summary crypto-summary">
          <div className="summary-card">
            <h3>🪙 Kripto Toplam Değer</h3>
            <p className="summary-value">₺{cryptoMetrics.cryptoTotalValue.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
          </div>
          <div className="summary-card">
            <h3>Kripto Kar/Zarar</h3>
            <p className={`summary-value ${cryptoMetrics.cryptoProfit >= 0 ? 'positive' : 'negative'}`}>
              ₺{cryptoMetrics.cryptoProfit.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </p>
          </div>
          <div className="summary-card">
            <h3>% Kar Zarar</h3>
            <p className={`summary-value ${cryptoMetrics.cryptoProfitPercent >= 0 ? 'positive' : 'negative'}`}>
              {cryptoMetrics.cryptoProfitPercent >= 0 ? '+' : ''}{fmtTR(cryptoMetrics.cryptoProfitPercent)}%
            </p>
          </div>
          <div className="summary-card">
            <h3>Maliyet</h3>
            <p className="summary-value">₺{cryptoMetrics.maliyet.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
          </div>
        </div>
      )}

      
      {/* Portfolio Type Filter */}
      <div className="portfolio-filter">
        <h3>Varlık Türü</h3>
        <div className="filter-buttons">
          <button
            className={`filter-button ${selectedType === 'all' ? 'active' : ''}`}
            onClick={() => setSelectedType('all')}
          >
            📊 Tümü ({portfolio.length})
          </button>
          <button
            className={`filter-button ${selectedType === 'stock' ? 'active' : ''}`}
            onClick={() => setSelectedType('stock')}
          >
            📈 Hisse ({portfolio.filter(item => item.type === 'stock').length})
          </button>
          <button
            className={`filter-button ${selectedType === 'currency' ? 'active' : ''}`}
            onClick={() => setSelectedType('currency')}
          >
            💱 Döviz ({portfolio.filter(item => item.type === 'currency').length})
          </button>
          <button
            className={`filter-button ${selectedType === 'gold' ? 'active' : ''}`}
            onClick={() => setSelectedType('gold')}
          >
            🏅 Altın ({portfolio.filter(item => item.type === 'gold').length})
          </button>
          <button
            className={`filter-button ${selectedType === 'crypto' ? 'active' : ''}`}
            onClick={() => setSelectedType('crypto')}
          >
            🪙 Kripto ({portfolio.filter(item => item.type === 'crypto').length})
          </button>
          <button
            className={`filter-button ${selectedType === 'deposit' ? 'active' : ''}`}
            onClick={() => setSelectedType('deposit')}
          >
            🏦 Banka Mevduatı ({portfolio.filter(item => item.type === 'deposit').length})
          </button>
        </div>
      </div>

      {/* Banka Mevduatı Ekleme Modalı */}
      {showDepositForm && (
        <div style={{position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', zIndex: 2000, background: 'rgba(0,0,0,0.15)'}} onClick={() => setShowDepositForm(false)}>
          <div style={{display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%'}}>
            <div onClick={e => e.stopPropagation()}>
              <BankDeposit onDepositAdded={handleDepositAdded} usdRate={getCurrentUSDRate()} />
            </div>
          </div>
        </div>
      )}

      {/* Portfolio Summary for Tümü */}
      {selectedType === 'all' && portfolio.length > 0 && (() => {
        const summary = calculateSummary();
        const totalCost = portfolio.reduce((sum, item) => sum + (item.buyPrice * item.quantity), 0);
        return (
          <div className="portfolio-overview-card">
            <div className="portfolio-overview-grid">
              <div className={`portfolio-overview-item ${(summary.dailyProfit || 0) >= 0 ? 'highlight-positive' : 'highlight-negative'}`}>
                <h4>Günlük Kar/Zarar (₺)</h4>
                <p className={`overview-value ${(summary.dailyProfit || 0) >= 0 ? 'positive' : 'negative'}`}>
                  {(summary.dailyProfit || 0) >= 0 ? '+' : ''}{(summary.dailyProfit || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
              </div>
              
              <div className={`portfolio-overview-item ${(summary.dailyProfitPercent || 0) >= 0 ? 'highlight-positive' : 'highlight-negative'}`}>
                <h4>Günlük % Değişim</h4>
                <p className={`overview-value ${(summary.dailyProfitPercent || 0) >= 0 ? 'positive' : 'negative'}`}>
                  {(summary.dailyProfitPercent || 0) >= 0 ? '+' : ''}{fmtTR((summary.dailyProfitPercent || 0))}%
                </p>
              </div>
              
              <div className="portfolio-overview-item">
                <h4>Varlık Sayısı</h4>
                <p className="overview-value neutral">{summary.itemsCount}</p>
              </div>
              
              <div className="portfolio-overview-item">
                <h4>Toplam Değer</h4>
                <p className="overview-value neutral">
                  ₺{summary.totalValue.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
              </div>
              
              <div className={`portfolio-overview-item ${(summary.totalProfit || 0) >= 0 ? 'highlight-positive' : 'highlight-negative'}`}>
                <h4>Kar/Zarar</h4>
                <p className={`overview-value ${(summary.totalProfit || 0) >= 0 ? 'positive' : 'negative'}`}>
                  {(summary.totalProfit || 0) >= 0 ? '+' : ''}₺{(summary.totalProfit || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
              </div>
              
              <div className={`portfolio-overview-item ${(summary.totalProfitPercent || 0) >= 0 ? 'highlight-positive' : 'highlight-negative'}`}>
                <h4>% Kar/Zarar</h4>
                <p className={`overview-value ${(summary.totalProfitPercent || 0) >= 0 ? 'positive' : 'negative'}`}>
                  {(summary.totalProfitPercent || 0) >= 0 ? '+' : ''}{fmtTR((summary.totalProfitPercent || 0))}%
                </p>
              </div>
              
              <div className="portfolio-overview-item">
                <h4>Maliyet</h4>
                <p className="overview-value muted">
                  ₺{totalCost.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Portfolio Allocation Pie Chart */}
      {selectedType === 'all' && portfolio.length > 0 && (() => {
        const COLORS = ['#4f46e5', '#059669', '#d97706', '#8b5cf6'];
        const LABELS: Record<string, string> = { stock: 'Hisse', currency: 'Döviz', gold: 'Altın', crypto: 'Kripto' };
        const typeMap: Record<string, number> = {};
        portfolio.forEach(item => {
          const val = item.totalValue || (item.currentPrice * item.quantity);
          typeMap[item.type] = (typeMap[item.type] || 0) + val;
        });
        const data = Object.entries(typeMap)
          .filter(([, v]) => v > 0)
          .map(([type, value]) => ({ name: LABELS[type] || type, value }));
        const total = data.reduce((s, d) => s + d.value, 0);

        return (
          <div className="portfolio-allocation-card">
            <h3 
              onClick={() => setShowAllocationChart(!showAllocationChart)}
              className="allocation-toggle"
              style={{ cursor: 'pointer', userSelect: 'none' }}
            >
              📊 Varlık Dağılımı
              <span style={{ marginLeft: '8px', fontSize: '14px', fontWeight: 'normal' }}>
                {showAllocationChart ? '▼' : '▶'}
              </span>
            </h3>
            {showAllocationChart && (
              <div className="allocation-chart-wrapper">
                <ResponsiveContainer width="100%" height={260}>
                  <PieChart>
                    <Pie
                      data={data}
                      cx="50%"
                      cy="50%"
                      innerRadius={55}
                      outerRadius={95}
                      paddingAngle={3}
                      dataKey="value"
                      label={({ name, value }) => `${name} %${((value / total) * 100).toFixed(0)}`}
                      labelLine={false}
                    >
                      {data.map((_, i) => (
                        <Cell key={i} fill={COLORS[i % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value: any) => `₺${fmtTR(Number(value))}`} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        );
      })()}

      {/* Sell Item Form - Modal */}
      {showSellForm && (
        <div className="modal-overlay" onClick={() => setShowSellForm(false)}>
          <div className="modal-content sell-form-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>💰 Satış Yap</h3>
              <button 
                className="modal-close"
                onClick={() => setShowSellForm(false)}
                title="Kapat"
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="sell-form">
                <div className="form-row">
                  <div>
                    <label>Varlık Seç</label>
                    <div className="symbol-input-container">
                      <input
                        type="text"
                        value={sellItem.symbol}
                        onChange={(e) => {
                          setSellItem({ ...sellItem, symbol: e.target.value.toUpperCase() });
                          setShowSellSymbolSuggestions(true);
                        }}
                        onFocus={() => setShowSellSymbolSuggestions(true)}
                        onBlur={() => setTimeout(() => setShowSellSymbolSuggestions(false), 200)}
                        placeholder="Varlık sembolünü yazın..."
                        required
                      />
                      {showSellSymbolSuggestions && filteredSellSuggestions.length > 0 && (
                        <div className="symbol-suggestions">
                          {filteredSellSuggestions.map((item, index) => (
                            <div
                              key={index}
                              className="suggestion-item"
                              onMouseDown={() => {
                                const rate = getCurrentUSDRate();
                                setSellItem({ 
                                  ...sellItem, 
                                  symbol: item.symbol,
                                  buyPrice: item.buyPrice,
                                  portfolioItemId: item.id, // Seçilen spesifik item'ı sakla
                                  usdRate: rate
                                });
                                setSellUsdRate(rate);
                                setShowSellSymbolSuggestions(false);
                              }}
                            >
                              <span className="suggestion-symbol">{item.symbol}</span>
                              <span className="suggestion-name">{item.displayText}</span>
                              <span className="suggestion-price">₺{fmtTR(item.buyPrice)}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  <div>
                    <label>Miktar</label>
                    <input
                      type="number"
                      placeholder={`Maks: ${sellItem.portfolioItemId ? portfolio.find(item => item.id === sellItem.portfolioItemId)?.quantity || 0 : 0}`}
                      value={sellItem.quantity}
                      onChange={(e) => setSellItem({ ...sellItem, quantity: e.target.value })}
                      min="0.01"
                      step="0.01"
                      max={sellItem.portfolioItemId ? portfolio.find(item => item.id === sellItem.portfolioItemId)?.quantity || 0 : 0}
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div>
                    <label>Satış Fiyatı (₺)</label>
                    <input
                      type="number"
                      placeholder="Satış fiyatı"
                      value={sellItem.sellPrice}
                      onChange={(e) => setSellItem({ ...sellItem, sellPrice: e.target.value })}
                      min="0.01"
                      step="0.01"
                    />
                  </div>
                  <div>
                    <label>Alış Fiyatı (₺)</label>
                    <input
                      type="number"
                      value={sellItem.buyPrice}
                      readOnly
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div>
                    <label>Satış Tarihi</label>
                    <input
                      type="date"
                      value={sellItem.sellDate}
                      onChange={(e) => setSellItem({ ...sellItem, sellDate: e.target.value })}
                      max={new Date().toISOString().split('T')[0]}
                    />
                  </div>
                  <div>
                    <label>USD Kuru</label>
                    <UsdRateInput
                      initialValue={sellUsdRate}
                      onRateChange={setSellUsdRate}
                      onRefresh={getCurrentUSDRate}
                    />
                  </div>
                </div>
                
                {/* Satış Bilgileri */}
                {sellItem.quantity && sellItem.sellPrice && sellItem.buyPrice && (
                  <div className="sell-info-box">
                    <div className="info-row">
                      <span>Toplam Satış Değeri:</span>
                      <span className="value">₺{(parseFloat(sellItem.quantity) * parseFloat(sellItem.sellPrice)).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                    </div>
                    <div className="info-row">
                      <span>Toplam Alış Değeri:</span>
                      <span className="value">₺{(parseFloat(sellItem.quantity) * parseFloat(sellItem.buyPrice)).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                    </div>
                    <div className="info-row">
                      <span>Realize Kar/Zarar:</span>
                      <span className={`value ${calculateSellProfit() >= 0 ? 'positive' : 'negative'}`}>
                        {calculateSellProfit() >= 0 ? '+' : ''}₺{Math.abs(calculateSellProfit()).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    </div>
                    <div className="info-row">
                      <span>% Getiri:</span>
                      <span className={`value ${calculateSellProfitPercent() >= 0 ? 'positive' : 'negative'}`}>
                        {calculateSellProfitPercent() >= 0 ? '+' : ''}{fmtTR(calculateSellProfitPercent())}%
                      </span>
                    </div>
                  </div>
                )}
                
                <div className="form-actions">
                  <button onClick={handleSellItem} className="sell-button">
                    💰 Satışı Yap
                  </button>
                  <button onClick={() => setShowSellForm(false)} className="cancel-button">
                    ❌ İptal
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Item Form */}
      {showAddForm && (
        <div className="add-item-form">
          <h3>Yeni Varlık Ekle</h3>
          <div className="form-row">
            <select
              value={newItem.type}
              onChange={(e) => {
                setNewItem({ ...newItem, type: e.target.value as any, symbol: '' });
                setShowSymbolSuggestions(false);
              }}
            >
              <option value="stock">📈 Hisse Senedi</option>
              <option value="currency">💱 Döviz</option>
              <option value="gold">🏅 Altın</option>
              <option value="crypto">🪙 Kripto Para</option>
            </select>
            <div className="symbol-input-container">
              <input
                type="text"
                placeholder="Sembol"
                value={newItem.symbol}
                onChange={(e) => {
                  setNewItem({ ...newItem, symbol: e.target.value.toUpperCase() });
                  setShowSymbolSuggestions(true);
                }}
                onFocus={() => setShowSymbolSuggestions(true)}
                onBlur={() => setTimeout(() => setShowSymbolSuggestions(false), 200)}
              />
              {showSymbolSuggestions && filteredSuggestions.length > 0 && (
                <div className="symbol-suggestions">
                  {filteredSuggestions.map((item, index) => (
                    <div
                      key={index}
                      className="suggestion-item"
                      onMouseDown={() => {
                        setNewItem({ ...newItem, symbol: item.symbol });
                        setShowSymbolSuggestions(false);
                      }}
                    >
                      <div className="suggestion-symbol">{item.symbol}</div>
                      <div className="suggestion-name">{item.name}</div>
                      <div className="suggestion-price">
                        {newItem.type === 'crypto' ? '$' : '₺'}{item.price?.toLocaleString()}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div className="form-row">
            <input
              type="number"
              placeholder="Adet veya Gram"
              value={newItem.quantity || ''}
              onChange={(e) => setNewItem({ ...newItem, quantity: e.target.value })}
              min="0.01"
              step="0.01"
            />
            <input
              type="number"
              placeholder="Birim Fiyat"
              value={newItem.buyPrice || ''}
              onChange={(e) => setNewItem({ ...newItem, buyPrice: e.target.value })}
              min="0.01"
              step="0.01"
            />
          </div>
          <div className="form-row with-usd-button">
            <input
              type="date"
              placeholder="Alım Tarihi"
              value={newItem.buyDate}
              onChange={(e) => setNewItem({ ...newItem, buyDate: e.target.value })}
              max={new Date().toISOString().split('T')[0]} // Gelecek tarih seçilemez
            />
            <div className="usd-input-container">
              <UsdRateInput
                initialValue={addUsdRate}
                onRateChange={setAddUsdRate}
                onRefresh={getCurrentUSDRate}
              />
            </div>
          </div>
          <div className="form-examples">
            <strong>Örnekler:</strong> 
            <span>THYAO - 100 adet - 279.80₺ | </span>
            <span>USD - 1000 adet - 32.15₺ | </span>
            <span>Çeyrek Altın - 2 adet - 1850₺ | </span>
            <span>BTC - 0.005 adet - 3456789₺</span>
          </div>
          <div className="form-actions">
            <button onClick={handleAddItem} className="confirm-button">✅ Ekle</button>
            <button onClick={() => setShowAddForm(false)} className="cancel-button">❌ İptal</button>
          </div>
        </div>
      )}

      {/* Portfolio Items */}
      {getGroupedFilteredPortfolio().length === 0 ? (
        <div className="empty-portfolio">
          <div className="empty-portfolio-icon">📊</div>
          <h3>{selectedType === 'all' ? 'Portföyünüz Boş' : `${getTypeName(selectedType)} Varlığınız Bulunmuyor`}</h3>
          <p>
            {selectedType === 'all' 
              ? 'Henüz yatırım varlığınız bulunmuyor.'
              : `Henüz ${getTypeName(selectedType).toLowerCase()} varlığınız bulunmuyor.`
            }
          </p>
          <div className="empty-portfolio-examples">
            <strong>Ekleyebileceğiniz örnek varlıklar:</strong><br/>
            📈 <strong>THYAO</strong> - 100 adet - 279.80₺<br/>
            💱 <strong>USD</strong> - 1000 adet - 32.15₺<br/>
            🏅 <strong>Çeyrek Altın</strong> - 2 adet - 1850₺<br/>
            🪙 <strong>BTC</strong> - 0.005 adet - 3456789₺
          </div>
          <p>Yukarıdaki "Yeni Ekle" butonuna tıklayarak başlayın!</p>
        </div>
      ) : (
        <div className="portfolio-items">
          <table>
            <thead>
              <tr>
                <th>Varlık Bilgisi</th>
                <th>Tür</th>
                <th>Miktar</th>
                <th>Ortalama Alış</th>
                <th>Mevcut Fiyat</th>
                <th>Toplam Değer</th>
                <th>Günlük Değişim</th>
                <th>Günlük %</th>
                <th>Kar/Zarar</th>
                <th>% Değişim</th>
                <th>Dolar Alsaydık<small>(TL Değer)</small></th>
                <th>Dolar %</th>
                <th>Alım Tarihi</th>
                <th>İşlem</th>
              </tr>
            </thead>
            <tbody>
              {getGroupedFilteredPortfolio().map((item) => {
                const key = `${item.type}-${item.symbol}`;
                const isExpanded = expandedItems.has(key);
                const hasMultipleItems = item.items.length > 1;
                // Calculate daily profit for this item
                const dailyProfitData = calculateDailyProfit(item);
                return (
                  <React.Fragment key={item.id}>
                    <tr 
                      className={hasMultipleItems ? 'grouped-row clickable' : ''}
                      onClick={() => hasMultipleItems && toggleExpanded(key)}
                    >
                      <td className="asset-name">
                        <span className="asset-icon">{getTypeIcon(item.type)}</span>
                        <div>
                          <div className="asset-symbol clickable" onClick={() => handleAssetClick(item)}>
                            {item.symbol}
                            {hasMultipleItems && <span className="item-count">({item.items.length})</span>}
                          </div>
                          <div className="asset-fullname">{item.name}</div>
                        </div>
                      </td>
                      <td>{getTypeName(item.type)}</td>
                      <td>{item.quantity}</td>
                      <td>₺{fmtTR(item.buyPrice)}</td>
                      <td>₺{fmtTR(item.currentPrice)}</td>
                      <td>₺{fmtTR(item.totalValue)}</td>
                      <td className={dailyProfitData.dailyProfit >= 0 ? 'positive' : 'negative'}>
                        {dailyProfitData.dailyProfit >= 0 ? '+' : ''}₺{fmtTR(Math.abs(dailyProfitData.dailyProfit))}
                      </td>
                      <td className={dailyProfitData.dailyProfitPercent >= 0 ? 'positive' : 'negative'}>
                        {dailyProfitData.dailyProfitPercent >= 0 ? '+' : ''}{fmtTR(dailyProfitData.dailyProfitPercent)}%
                      </td>
                      <td className={item.profit >= 0 ? 'positive' : 'negative'}>
                        {item.profit >= 0 ? '+' : ''}₺{fmtTR(Math.abs(item.profit))}
                      </td>
                      <td className={item.profitPercent >= 0 ? 'positive' : 'negative'}>
                        {item.profitPercent >= 0 ? '+' : ''}{fmtTR(item.profitPercent)}%
                      </td>
                      {(() => {
                        // DEPOSIT (MEVDUAT) SATIRI İÇİN "Dolar alsaydık" hesaplaması
                        if (item.type === 'deposit') {
                          const amount = item.buyPrice;
                          const usdRate = parseFloat(typeof item.usdRate === 'string' ? item.usdRate : String(item.usdRate)) || 1;
                          const currentUsdRate = parseFloat(getCurrentUSDRate()) || usdRate;
                          const dollarAmount = amount / usdRate;
                          const tlIfUsd = dollarAmount * currentUsdRate;
                          const diff = tlIfUsd - amount;
                          const diffPercent = amount > 0 ? ((tlIfUsd - amount) / amount) * 100 : 0;
                          return (
                            <>
                              <td className={diff >= 0 ? 'positive' : 'negative'}>
                                {diff >= 0 ? '+' : ''}₺{fmtTR(Math.abs(diff))}
                                <div className="asset-fullname">${fmtTR(dollarAmount)}</div>
                              </td>
                              <td className={diffPercent >= 0 ? 'positive' : 'negative'}>
                                {diffPercent >= 0 ? '+' : ''}{fmtTR(diffPercent)}%
                              </td>
                            </>
                          );
                        }
                        // Standart satırlar için mevcut "Dolar alsaydık" hesaplaması
                        const currentUsdRate = parseFloat(getCurrentUSDRate()) || item.usdRate;
                        const totalCost = item.buyPrice * item.quantity;
                        const usdAmount = totalCost / item.usdRate;
                        const usdCurrentValue = usdAmount * currentUsdRate;
                        const usdAltProfit = item.totalValue - usdCurrentValue;
                        const usdAltProfitPercent = usdCurrentValue > 0 ? (usdAltProfit / usdCurrentValue) * 100 : 0;
                        const isCurrency = item.type === 'currency' && item.symbol === 'USD';
                        const investmentBetter = usdAltProfit >= 0;
                        return isCurrency ? (
                          <><td>-</td><td>-</td></>
                        ) : (
                          <>
                            <td className={investmentBetter ? 'positive' : 'negative'}>
                              {usdAltProfit >= 0 ? '+' : '-'}₺{fmtTR(Math.abs(usdAltProfit))}
                              <div className="asset-fullname">${fmtTR(usdAmount)}</div>
                            </td>
                            <td className={investmentBetter ? 'positive' : 'negative'}>
                              {usdAltProfitPercent >= 0 ? '+' : ''}{fmtTR(usdAltProfitPercent)}%
                            </td>
                          </>
                        );
                      })()}
                      <td className="buy-date">
                        {new Date(item.buyDate).toLocaleDateString('tr-TR', { 
                          day: '2-digit', 
                          month: '2-digit', 
                          year: 'numeric' 
                        })}
                      </td>
                      <td>
                        <div className="action-buttons">
                          <button 
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEditItem(item);
                            }}
                            className="action-btn edit"
                            title="Düzenle"
                          >
                            ✏️
                          </button>
                          <button 
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSellSpecificItem(item);
                            }}
                            className="action-btn sell"
                            title="Sat"
                          >
                            💰
                          </button>
                          <button 
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteItem(item.id);
                            }}
                            className="action-btn delete"
                            title="Sil"
                          >
                            🗑️
                          </button>
                        </div>
                      </td>
                    </tr>
                    {isExpanded && hasMultipleItems && item.items.map((subItem, index) => (
                      <tr key={subItem.id} className="detail-row">
                        <td className="asset-name">
                          <span className="detail-icon">📝</span>
                          <div>
                            <div className="asset-symbol clickable" onClick={() => handleAssetClick(subItem)}>
                              {subItem.symbol}
                            </div>
                            <div className="asset-fullname">Alım: {new Date(subItem.buyDate).toLocaleDateString('tr-TR')}</div>
                          </div>
                        </td>
                        <td>{getTypeName(subItem.type)}</td>
                        <td>{subItem.quantity}</td>
                        <td>₺{fmtTR(subItem.buyPrice)}</td>
                        <td>₺{fmtTR(subItem.currentPrice)}</td>
                        <td>₺{fmtTR(subItem.totalValue)}</td>
                        {(() => {
                          const dailyProfit = calculateDailyProfit(subItem);
                          return (
                            <>
                              <td className={dailyProfit.dailyProfit >= 0 ? 'positive' : 'negative'}>
                                {dailyProfit.dailyProfit >= 0 ? '+' : ''}₺{fmtTR(Math.abs(dailyProfit.dailyProfit))}
                              </td>
                              <td className={dailyProfit.dailyProfitPercent >= 0 ? 'positive' : 'negative'}>
                                {dailyProfit.dailyProfitPercent >= 0 ? '+' : ''}{fmtTR(dailyProfit.dailyProfitPercent)}%
                              </td>
                            </>
                          );
                        })()}
                        <td className={subItem.profit >= 0 ? 'positive' : 'negative'}>
                          ₺{fmtTR(Math.abs(subItem.profit))}
                        </td>
                        <td className={subItem.profitPercent >= 0 ? 'positive' : 'negative'}>
                          {subItem.profitPercent >= 0 ? '+' : ''}{fmtTR(subItem.profitPercent)}%
                        </td>
                        {(() => {
                          const currentUsdRate = parseFloat(getCurrentUSDRate()) || subItem.usdRate;
                          const subTotalCost = subItem.buyPrice * subItem.quantity;
                          const subUsdAmount = subTotalCost / subItem.usdRate;
                          const subUsdCurrentValue = subUsdAmount * currentUsdRate;
                          const subUsdAltProfit = subItem.totalValue - subUsdCurrentValue;
                          const subUsdAltProfitPercent = subUsdCurrentValue > 0 ? (subUsdAltProfit / subUsdCurrentValue) * 100 : 0;
                          const isCurrency = subItem.type === 'currency' && subItem.symbol === 'USD';
                          const subInvestmentBetter = subUsdAltProfit >= 0;
                          return isCurrency ? (
                            <><td>-</td><td>-</td></>
                          ) : (
                            <>
                              <td className={subInvestmentBetter ? 'positive' : 'negative'}>
                                {subUsdAltProfit >= 0 ? '+' : '-'}₺{fmtTR(Math.abs(subUsdAltProfit))}
                                <div className="asset-fullname">${fmtTR(subUsdAmount)}</div>
                              </td>
                              <td className={subInvestmentBetter ? 'positive' : 'negative'}>
                                {subUsdAltProfitPercent >= 0 ? '+' : ''}{fmtTR(subUsdAltProfitPercent)}%
                              </td>
                            </>
                          );
                        })()}
                        <td className="buy-date">
                          {new Date(subItem.buyDate).toLocaleDateString('tr-TR', { 
                            day: '2-digit', 
                            month: '2-digit', 
                            year: 'numeric' 
                          })}
                        </td>
                        <td>
                          <div className="action-buttons">
                            <button 
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEditItem(subItem);
                              }}
                              className="action-btn edit"
                              title="Düzenle"
                            >
                              ✏️
                            </button>
                            <button 
                              onClick={(e) => {
                                e.stopPropagation();
                                handleSellSpecificItem(subItem);
                              }}
                              className="action-btn sell"
                              title="Sat"
                            >
                              💰
                            </button>
                            <button 
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteItem(subItem.id);
                              }}
                              className="action-btn delete"
                              title="Sil"
                            >
                              🗑️
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Sell Transactions History */}
      <div className="portfolio-section">
        <div className="section-header">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
            <h3>Satış Geçmişi</h3>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <button 
                className="toggle-button"
                onClick={() => setShowSellHistory(!showSellHistory)}
              >
                {showSellHistory ? 'Gizle' : 'Göster'}
              </button>
              <button 
                className="bulk-upload-btn"
                onClick={() => setShowSellExcelUpload(true)}
                title="Toplu Satış Yükle"
              >
                📊 Toplu Yükle
              </button>
            </div>
          </div>
        </div>
        
        {showSellHistory && (
          <div>
            {/* Sell Type Filter */}
            <div className="portfolio-filter sell-filter">
              <h4>Varlık Türü</h4>
              <div className="filter-buttons">
                <button
                  className={`filter-button ${selectedSellType === 'all' ? 'active' : ''}`}
                  onClick={() => setSelectedSellType('all')}
                >
                  📊 Tümü ({sellTransactions.length})
                </button>
                <button
                  className={`filter-button ${selectedSellType === 'stock' ? 'active' : ''}`}
                  onClick={() => setSelectedSellType('stock')}
                >
                  📈 Hisse ({sellTransactions.filter(t => t.type === 'stock').length})
                </button>
                <button
                  className={`filter-button ${selectedSellType === 'currency' ? 'active' : ''}`}
                  onClick={() => setSelectedSellType('currency')}
                >
                  💱 Döviz ({sellTransactions.filter(t => t.type === 'currency').length})
                </button>
                <button
                  className={`filter-button ${selectedSellType === 'gold' ? 'active' : ''}`}
                  onClick={() => setSelectedSellType('gold')}
                >
                  🏅 Altın ({sellTransactions.filter(t => t.type === 'gold').length})
                </button>
                <button
                  className={`filter-button ${selectedSellType === 'crypto' ? 'active' : ''}`}
                  onClick={() => setSelectedSellType('crypto')}
                >
                  🪙 Kripto ({sellTransactions.filter(t => t.type === 'crypto').length})
                </button>
              </div>
            </div>
            
            <div className="sell-summary">
              <div className="summary-item">
                <h4>Toplam Satış Değeri</h4>
                <p className="positive">
                  ₺{filteredSellTransactions.reduce((sum, t) => sum + (t.sellPrice * t.quantity), 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
              </div>
              <div className="summary-item">
                <h4>Toplam Kar/Zarar</h4>
                <p className={filteredSellTransactions.reduce((sum, t) => sum + t.realizedProfit, 0) >= 0 ? 'positive' : 'negative'}>
                  {filteredSellTransactions.reduce((sum, t) => sum + t.realizedProfit, 0) >= 0 ? '+' : ''}₺{filteredSellTransactions.reduce((sum, t) => sum + t.realizedProfit, 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
              </div>
              <div className="summary-item">
                <h4>Toplam % Getiri</h4>
                <p className={filteredSellTransactions.reduce((sum, t) => sum + t.realizedProfit, 0) >= 0 ? 'positive' : 'negative'}>
                  {(() => {
                    const totalCost = filteredSellTransactions.reduce((sum, t) => sum + (t.buyPrice * t.quantity), 0);
                    const totalProfit = filteredSellTransactions.reduce((sum, t) => sum + t.realizedProfit, 0);
                    const totalProfitPercent = totalCost > 0 ? (totalProfit / totalCost) * 100 : 0;
                    return (totalProfitPercent >= 0 ? '+' : '') + fmtTR(totalProfitPercent) + '%';
                  })()}
                </p>
              </div>
              <div className="summary-item">
                <h4>İşlem Sayısı</h4>
                <p>{filteredSellTransactions.length}</p>
              </div>
            </div>
            
            {filteredSellTransactions.length === 0 ? (
              <div className="empty-transactions">
                <div className="empty-transactions-icon">📋</div>
                <h4>Satış Geçmişi Boş</h4>
                <p>{selectedSellType === 'all' ? 'Henüz satış işlemi yapmadınız.' : `${getTypeName(selectedSellType)} türünde henüz satış işlemi yapmadınız.`}</p>
                <p>Portföydeki varlıkların yanındaki 💰 "Sat" butonunu kullanarak satış yapabilirsiniz.</p>
              </div>
            ) : (
              <div className="sell-transactions-table">
                <table>
                  <thead>
                    <tr>
                      <th>Varlık</th>
                      <th>Tür</th>
                      <th>Miktar</th>
                      <th>Satış Fiyatı</th>
                      <th>Alış Fiyatı</th>
                      <th>Realize Kar/Zarar</th>
                      <th>% Getiri</th>
                      <th>Satış Tarihi</th>
                      <th>İşlem</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredSellTransactions.map((transaction) => (
                      <tr key={transaction.id}>
                        <td className="asset-name">
                          <span className="asset-icon">{getTypeIcon(transaction.type)}</span>
                          <div>
                            <strong>{transaction.symbol}</strong>
                            <br/>
                            <small>{transaction.name}</small>
                          </div>
                        </td>
                        <td>
                          <span className="type-badge">{getTypeName(transaction.type)}</span>
                        </td>
                        <td>{transaction.quantity}</td>
                        <td>₺{transaction.sellPrice.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                        <td>₺{transaction.buyPrice.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                        <td className={transaction.realizedProfit >= 0 ? 'positive' : 'negative'}>
                          ₺{transaction.realizedProfit.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </td>
                        <td className={transaction.realizedProfitPercent >= 0 ? 'positive' : 'negative'}>
                          {transaction.realizedProfitPercent >= 0 ? '+' : ''}{fmtTR(transaction.realizedProfitPercent)}%
                        </td>
                        <td>{new Date(transaction.sellDate).toLocaleDateString('tr-TR')}</td>
                        <td>
                          <div className="action-buttons">
                            <button 
                              onClick={() => handleEditSellTransaction(transaction)}
                              className="edit-button small"
                              title="Bu satışı düzenle"
                            >
                              Düzenle
                            </button>
                            <button 
                              onClick={() => handleDeleteSellTransaction(transaction.id)}
                              className="delete-button small"
                              title="Bu satışı sil"
                            >
                              Sil
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                
                {/* Satış Özeti */}
                <div className="sell-summary">
                  <div className="summary-item">
                    <h4>Toplam Satış Değeri</h4>
                    <p className="positive">
                      ₺{filteredSellTransactions.reduce((sum, t) => sum + (t.sellPrice * t.quantity), 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </p>
                  </div>
                  <div className="summary-item">
                    <h4>Toplam Kar/Zarar</h4>
                    <p className={filteredSellTransactions.reduce((sum, t) => sum + t.realizedProfit, 0) >= 0 ? 'positive' : 'negative'}>
                      {filteredSellTransactions.reduce((sum, t) => sum + t.realizedProfit, 0) >= 0 ? '+' : ''}₺{filteredSellTransactions.reduce((sum, t) => sum + t.realizedProfit, 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </p>
                  </div>
                  <div className="summary-item">
                    <h4>İşlem Sayısı</h4>
                    <p>{filteredSellTransactions.length}</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Excel Upload Modal */}
      {showExcelUpload && (
        <div className="modal-overlay" onClick={() => setShowExcelUpload(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>📊 Excel ile Toplu Ekle</h3>
              <button 
                className="modal-close"
                onClick={() => setShowExcelUpload(false)}
                title="Kapat"
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <ExcelUpload 
                onImport={handleExcelImport}
                existingItems={portfolio}
              />
            </div>
          </div>
        </div>
      )}

      {/* Edit Item Form */}
      {showEditForm && editingItem && (
        <div className="modal-overlay" onClick={() => setShowEditForm(false)}>
          <div className="modal-content edit-form-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>✏️ Varlık Düzenle</h3>
              <button 
                className="modal-close"
                onClick={() => setShowEditForm(false)}
                title="Kapat"
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="form-row">
                <div>
                  <label>Sembol</label>
                  <div className="symbol-input-container">
                    <input
                      type="text"
                      value={editingItem.symbol}
                      onChange={(e) => {
                        setEditingItem({ ...editingItem, symbol: e.target.value.toUpperCase() });
                        setShowEditSymbolSuggestions(true);
                      }}
                      onFocus={() => setShowEditSymbolSuggestions(true)}
                      placeholder="Sembol arayın veya seçin"
                    />
                    {showEditSymbolSuggestions && filteredEditSymbolSuggestions.length > 0 && (
                      <div className="symbol-suggestions">
                        {filteredEditSymbolSuggestions.slice(0, 10).map((item, index) => (
                          <div
                            key={index}
                            className="symbol-suggestion-item"
                            onClick={() => {
                              setEditingItem({ ...editingItem, symbol: item.symbol });
                              setShowEditSymbolSuggestions(false);
                            }}
                          >
                            <div className="suggestion-symbol">{item.symbol}</div>
                            <div className="suggestion-name">{item.name}</div>
                            <div className="suggestion-price">
                              {editingItem.type === 'crypto' ? '$' : '₺'}{item.price?.toLocaleString()}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <div>
                  <label>Miktar</label>
                  <input
                    type="number"
                    value={editingItem.quantity}
                    onChange={(e) => setEditingItem({ ...editingItem, quantity: parseFloat(e.target.value) || 0 })}
                    min="0.01"
                    step="0.01"
                  />
                </div>
              </div>
              <div className="form-row">
                <div>
                  <label>Alış Fiyatı (₺)</label>
                  <input
                    type="number"
                    value={editingItem.buyPrice}
                    onChange={(e) => setEditingItem({ ...editingItem, buyPrice: parseFloat(e.target.value) || 0 })}
                    min="0.01"
                    step="0.01"
                  />
                </div>
                <div>
                  <label>Alım Tarihi</label>
                  <input
                    type="date"
                    value={editingItem.buyDate}
                    onChange={(e) => setEditingItem({ ...editingItem, buyDate: e.target.value })}
                    max={new Date().toISOString().split('T')[0]}
                  />
                </div>
              </div>
              <div className="form-actions">
                <button onClick={handleUpdateItem} className="confirm-button">✅ Kaydet</button>
                <button onClick={handleCancelEdit} className="cancel-button">❌ İptal</button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Asset Detail Modal */}
      <AssetDetailModal
        isOpen={showAssetDetail}
        onClose={() => setShowAssetDetail(false)}
        asset={selectedAsset}
      />

      {/* Sell Transaction Excel Upload Modal */}
      {showSellExcelUpload && (
        <SellTransactionExcelUpload
          onImport={handleSellExcelImport}
          onClose={() => setShowSellExcelUpload(false)}
        />
      )}

      {/* Sell Transaction Edit Modal */}
      {showSellEditForm && editingSellTransaction && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>✏️ Satış İşlemini Düzenle</h3>
              <button className="modal-close" onClick={handleCancelSellEdit}>×</button>
            </div>
            
            <div className="modal-body">
              <div className="form-group">
                <label>Varlık:</label>
                <input
                  type="text"
                  value={editingSellTransaction.symbol}
                  onChange={(e) => setEditingSellTransaction({
                    ...editingSellTransaction,
                    symbol: e.target.value,
                    name: e.target.value
                  })}
                  className="form-input"
                />
              </div>
              
              <div className="form-group">
                <label>Tür:</label>
                <select
                  value={editingSellTransaction.type}
                  onChange={(e) => setEditingSellTransaction({
                    ...editingSellTransaction,
                    type: e.target.value as 'stock' | 'currency' | 'gold' | 'crypto'
                  })}
                  className="form-input"
                >
                  <option value="stock">Hisse</option>
                  <option value="currency">Döviz</option>
                  <option value="gold">Altın</option>
                  <option value="crypto">Kripto</option>
                </select>
              </div>
              
              <div className="form-group">
                <label>Miktar:</label>
                <input
                  type="number"
                  value={editingSellTransaction.quantity}
                  onChange={(e) => setEditingSellTransaction({
                    ...editingSellTransaction,
                    quantity: parseFloat(e.target.value) || 0
                  })}
                  className="form-input"
                  min="0"
                  step="0.01"
                />
              </div>
              
              <div className="form-group">
                <label>Satış Fiyatı (₺):</label>
                <input
                  type="number"
                  value={editingSellTransaction.sellPrice}
                  onChange={(e) => setEditingSellTransaction({
                    ...editingSellTransaction,
                    sellPrice: parseFloat(e.target.value) || 0,
                    realizedProfit: (parseFloat(e.target.value) || 0) * editingSellTransaction.quantity - editingSellTransaction.buyPrice * editingSellTransaction.quantity,
                    realizedProfitPercent: editingSellTransaction.buyPrice > 0 ? 
                      ((parseFloat(e.target.value) || 0) - editingSellTransaction.buyPrice) / editingSellTransaction.buyPrice * 100 : 0
                  })}
                  className="form-input"
                  min="0"
                  step="0.01"
                />
              </div>
              
              <div className="form-group">
                <label>Alış Fiyatı (₺):</label>
                <input
                  type="number"
                  value={editingSellTransaction.buyPrice}
                  onChange={(e) => setEditingSellTransaction({
                    ...editingSellTransaction,
                    buyPrice: parseFloat(e.target.value) || 0,
                    realizedProfit: editingSellTransaction.sellPrice * editingSellTransaction.quantity - (parseFloat(e.target.value) || 0) * editingSellTransaction.quantity,
                    realizedProfitPercent: (parseFloat(e.target.value) || 0) > 0 ? 
                      (editingSellTransaction.sellPrice - (parseFloat(e.target.value) || 0)) / (parseFloat(e.target.value) || 0) * 100 : 0
                  })}
                  className="form-input"
                  min="0"
                  step="0.01"
                />
              </div>
              
              <div className="form-group">
                <label>Satış Tarihi:</label>
                <input
                  type="date"
                  value={editingSellTransaction.sellDate}
                  onChange={(e) => setEditingSellTransaction({
                    ...editingSellTransaction,
                    sellDate: e.target.value
                  })}
                  className="form-input"
                  max={new Date().toISOString().split('T')[0]}
                />
              </div>
              
              <div className="profit-summary">
                <h4>Kar/Zarar Hesaplaması</h4>
                <div className="profit-details">
                  <p>Satış Değeri: <span className="positive">₺{fmtTR((editingSellTransaction.sellPrice * editingSellTransaction.quantity))}</span></p>
                  <p>Alış Değeri: <span className="negative">₺{fmtTR((editingSellTransaction.buyPrice * editingSellTransaction.quantity))}</span></p>
                  <p>Realize Kar/Zarar: <span className={editingSellTransaction.realizedProfit >= 0 ? 'positive' : 'negative'}>
                    ₺{fmtTR(editingSellTransaction.realizedProfit)}
                  </span></p>
                  <p>Getiri Yüzdesi: <span className={editingSellTransaction.realizedProfitPercent >= 0 ? 'positive' : 'negative'}>
                    {editingSellTransaction.realizedProfitPercent >= 0 ? '+' : ''}{fmtTR(editingSellTransaction.realizedProfitPercent)}%
                  </span></p>
                </div>
              </div>
              
              <div className="form-actions">
                <button onClick={handleUpdateSellTransaction} className="confirm-button">
                  ✅ Kaydet
                </button>
                <button onClick={handleCancelSellEdit} className="cancel-button">
                  ❌ İptal
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Portfolio;
